import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, Suspense, lazy } from 'react';
import ReactDOM from 'react-dom';
// MapView is lazy-loaded to defer Leaflet initialization until after mount (fixes iOS/WKWebView startup crash)
import { apiService } from './services/api';
import { useStore, SESSION_ID } from './store';
import { useTheme } from './theme';
import {
  Mosque, PrayerSpot, PrayerTime, JumuahSession, SpecialPrayer,
  STATUS_CONFIG, SPOT_TYPE_LABELS, SUGGESTION_FIELD_LABELS,
  SpotSubmitRequest, MosqueSuggestion,
  TravelPairPlan, TravelOption, TravelDestination, TravelStop, GeocodeSuggestion,
  TripItinerary, PairChoice,
} from './types';

const MapView = lazy(() => import('./components/MapView'));

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Parse a Google Maps or Apple Maps share URL and extract lat/lng + place name.
 * Returns null if the URL doesn't contain parseable coordinates.
 */
function parseMapShareUrl(rawUrl: string): { lat: number; lng: number; place_name: string } | null {
  try {
    // Google Maps place URL: /maps/place/Name/@lat,lng,zoom
    const placeMatch = rawUrl.match(/\/maps\/place\/([^/@]+)\/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (placeMatch) {
      return {
        place_name: decodeURIComponent(placeMatch[1]).replace(/\+/g, ' '),
        lat: parseFloat(placeMatch[2]),
        lng: parseFloat(placeMatch[3]),
      };
    }

    const u = new URL(rawUrl);

    // Apple Maps: ?ll=lat,lng
    const ll = u.searchParams.get('ll');
    if (ll) {
      const llMatch = ll.match(/(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (llMatch) {
        const name = u.searchParams.get('q') ?? u.searchParams.get('address') ?? 'Shared destination';
        return { lat: parseFloat(llMatch[1]), lng: parseFloat(llMatch[2]), place_name: name };
      }
    }

    // Google Maps: ?q=lat,lng
    const q = u.searchParams.get('q') ?? u.searchParams.get('query');
    if (q) {
      const coordMatch = q.match(/^(-?\d+\.\d+),(-?\d+\.\d+)$/);
      if (coordMatch) {
        return { lat: parseFloat(coordMatch[1]), lng: parseFloat(coordMatch[2]), place_name: 'Shared destination' };
      }
    }
  } catch {}
  return null;
}

function fmtTime(t: string | null): string {
  if (!t) return '—';
  try {
    // Handle plain "HH:MM" or "HH:MM:SS" from the backend
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(t)) {
      const [h, m] = t.split(':').map(Number);
      const d = new Date();
      d.setHours(h, m, 0, 0);
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    // Fall back to ISO string parsing
    return new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return t;
  }
}

// Strip emoji characters from server-supplied strings
function stripEmoji(s: string): string {
  return s.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim();
}

function fmtDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const days  = Math.floor(m / (24 * 60));
  const hours = Math.floor((m % (24 * 60)) / 60);
  const mins  = m % 60;
  if (days > 0) {
    const parts = [`${days} day${days > 1 ? 's' : ''}`];
    if (hours) parts.push(`${hours}h`);
    if (mins)  parts.push(`${mins}min`);
    return parts.join(' ');
  }
  if (hours > 0) return mins ? `${hours}h ${mins}min` : `${hours}h`;
  return `${mins}min`;
}

const WEBSITE_SOURCES = new Set([
  'mosque_website_html', 'mosque_website_js', 'mosque_website',
  'static_html', 'playwright_html', 'vision_ai',
]);

const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

interface MapPoint {
  lat: number;
  lng: number;
  name?: string;
  place_id?: string | null;
  is_gps?: boolean;  // true = use device current location instead of explicit coords
}

function buildGoogleMapsUrl(points: MapPoint[]): string {
  // Use the directions URL format: /maps/dir/origin/.../destination
  // Empty first segment when origin is GPS — Google uses current device location
  // Use place_id when available; fall back to name+address search query for a named pin
  const segments = points.map(p => {
    if (p.is_gps) return '';
    if (p.place_id) return `place_id:${p.place_id}`;
    if (p.name) return encodeURIComponent(p.name);
    return `${p.lat},${p.lng}`;
  });
  return 'https://www.google.com/maps/dir/' + segments.join('/');
}

function buildAppleMapsUrl(points: MapPoint[]): string {
  // Apple Maps: saddr=Name@lat,lng  daddr=Name@lat,lng (repeated)
  // Omit saddr when origin is GPS — Apple Maps uses current device location
  function applePoint(p: MapPoint): string {
    if (p.name) return `${encodeURIComponent(p.name)}@${p.lat},${p.lng}`;
    return `${p.lat},${p.lng}`;
  }
  const [origin, ...rest] = points;
  const destParts = rest.map(p => `daddr=${applePoint(p)}`).join('&');
  if (origin.is_gps) {
    return `https://maps.apple.com/?${destParts}&dirflg=d`;
  }
  return `https://maps.apple.com/?saddr=${applePoint(origin)}&${destParts}&dirflg=d`;
}

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function dataSourceBadge(prayers: { adhan_source?: string | null; iqama_source?: string | null; data_freshness?: string | null }[]): {
  label: string; color: string; title?: string;
} {
  if (!prayers.length) return { label: '~ Estimated times', color: 'text-amber-600' };
  // Look at non-fajr prayers for source info (fajr often has different sources)
  const relevant = prayers.filter(p => !['fajr'].includes((p as any).prayer));
  const sample = relevant.length ? relevant[0] : prayers[0];
  const adhanSrc = (sample as any).adhan_source ?? '';
  const iqamaSrc = (sample as any).iqama_source ?? '';
  const freshness = (sample as any).data_freshness ?? null;

  const adhanVerified = WEBSITE_SOURCES.has(adhanSrc);
  const iqamaVerified = WEBSITE_SOURCES.has(iqamaSrc);
  const freshSuffix = freshness ? ` · ${freshness}` : '';

  if (adhanVerified && iqamaVerified) {
    return { label: `✓ From mosque website${freshSuffix}`, color: 'text-green-700' };
  }
  if (adhanSrc === 'mawaqit_api' || iqamaSrc === 'mawaqit_api') {
    return { label: `From Mawaqit${freshSuffix}`, color: 'text-green-700' };
  }
  if (adhanSrc === 'islamicfinder' || iqamaSrc === 'islamicfinder') {
    return { label: `From IslamicFinder${freshSuffix}`, color: 'text-gray-500' };
  }
  if (adhanSrc === 'user_submitted' || iqamaSrc === 'user_submitted') {
    return { label: `Community-submitted${freshSuffix}`, color: 'text-blue-600' };
  }
  if (adhanVerified && !iqamaVerified) {
    return {
      label: '~ Iqama estimated',
      color: 'text-amber-600',
      title: 'Adhan time from mosque website, iqama time is estimated',
    };
  }
  return {
    label: '~ Estimated times — help us get real times',
    color: 'text-amber-600',
    title: 'Congregation time not confirmed — based on calculated prayer window',
  };
}

// Canadian IANA timezone IDs — everything else is treated as US (miles)
const CANADIAN_TIMEZONES = new Set([
  'America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg',
  'America/Halifax', 'America/St_Johns', 'America/Moncton', 'America/Regina',
  'America/Glace_Bay', 'America/Goose_Bay', 'America/Blanc-Sablon',
  'America/Nipigon', 'America/Thunder_Bay', 'America/Rainy_River',
  'America/Cambridge_Bay', 'America/Iqaluit', 'America/Rankin_Inlet',
  'America/Resolute', 'America/Creston', 'America/Dawson', 'America/Dawson_Creek',
  'America/Fort_Nelson', 'America/Inuvik', 'America/Whitehorse', 'America/Pangnirtung',
]);

const USE_METRIC = CANADIAN_TIMEZONES.has(Intl.DateTimeFormat().resolvedOptions().timeZone);

function distLabel(meters: number): string {
  if (USE_METRIC) {
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  } else {
    const miles = meters / 1609.344;
    if (miles < 0.1) return `${Math.round(meters)} ft`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (miles < 10) return `${miles.toFixed(1)} mi`;
    return `${Math.round(miles)} mi`;
  }
}

// ─── Prayer row ordering helpers ────────────────────────────────────────────

const ACTIVE_STATUSES = new Set([
  'can_catch_with_imam', 'can_catch_with_imam_in_progress',
  'can_pray_solo_at_mosque', 'pray_at_nearby_location',
]);

/** Parse "HH:MM" → minutes since midnight, or null. */
function hhmmToMin(t: string | null | undefined): number | null {
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1]) * 60 + parseInt(m[2]);
}

/**
 * Returns true if we are past the switch point for the active prayer —
 * meaning the NEXT prayer should be shown as primary (more urgent).
 *
 * Switch point = midpoint of [adhan_time, period_ends_at] for the active prayer,
 * computed dynamically from today's actual times (varies by season and location).
 * Exception: Isha uses midnight (00:00) instead of the geometric midpoint.
 */
function isPastSwitchPoint(active: { prayer: string; adhan_time: string | null; period_ends_at: string | null }): boolean {
  const adhan = hhmmToMin(active.adhan_time);
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  if (adhan === null) return false;

  // Isha: switch at midnight — "past midnight" means nowMin < adhan (we crossed midnight)
  if (active.prayer === 'isha') return nowMin < adhan;

  let periodEnd = hhmmToMin(active.period_ends_at);
  if (periodEnd === null) return false;
  if (periodEnd < adhan) periodEnd += 1440; // midnight wrap

  const switchPoint = (adhan + periodEnd) / 2;

  // Normalize nowMin: if before adhan, we're in the "next-day" portion → add 1440
  const nowNorm = nowMin >= adhan ? nowMin : nowMin + 1440;
  return nowNorm > switchPoint;
}

// ─── Mosque Card ────────────────────────────────────────────────────────────

function MosqueCard({ mosque }: { mosque: Mosque }) {
  const openSheet           = useStore((s) => s.openSheet);
  const setSelectedMosqueId = useStore((s) => s.setSelectedMosqueId);
  const setSingleMosqueNav  = useStore((s) => s.setSingleMosqueNav);
  const setBottomSheetHeight = useStore((s) => s.setBottomSheetHeight);
  const prayedToday         = useStore((s) => s.prayedToday);
  const travelMode          = useStore((s) => s.travelMode);
  const th                  = useTheme();
  const badge               = dataSourceBadge(mosque.prayers);

  const catchable = (mosque.catchable_prayers?.length
    ? mosque.catchable_prayers
    : mosque.next_catchable ? [mosque.next_catchable] : []);

  // In Musafir mode, praying the later prayer implies the earlier is done too
  const effectivePrayed = new Set(prayedToday);
  if (travelMode) {
    if (effectivePrayed.has('asr'))  effectivePrayed.add('dhuhr');
    if (effectivePrayed.has('isha')) effectivePrayed.add('maghrib');
  }

  // Remove prayers the user already marked as prayed (including implied ones)
  let visible = catchable.filter(p => !effectivePrayed.has(p.prayer));

  // If all catchable prayers are prayed, find the next unprayed prayer starting from
  // the current prayer period (not from Fajr). This avoids showing Fajr when Dhuhr is prayed.
  if (visible.length === 0) {
    const PRAYER_ORDER = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
    const prayerMap = new Map((mosque.prayers ?? []).map(p => [p.prayer, p]));
    const nowMin = new Date().getHours() * 60 + new Date().getMinutes();

    // Build ordered prayers with adhan minutes
    const ordered = PRAYER_ORDER
      .map(name => prayerMap.get(name))
      .filter((p): p is NonNullable<typeof p> => !!p && !!p.adhan_time)
      .map(p => {
        const [h, m] = p.adhan_time!.split(':').map(Number);
        return { ...p, adhanMin: h * 60 + m };
      });

    // Find the current prayer period (last prayer whose adhan has passed)
    let startIdx = 0;
    for (let i = ordered.length - 1; i >= 0; i--) {
      if (ordered[i].adhanMin <= nowMin) { startIdx = i; break; }
    }

    // From the current period forward, find the first unprayed prayer
    let nextPrayer: typeof ordered[0] | null = null;
    for (let i = startIdx; i < ordered.length; i++) {
      if (!effectivePrayed.has(ordered[i].prayer)) { nextPrayer = ordered[i]; break; }
    }

    if (nextPrayer) {
      const isUpcoming = nextPrayer.adhanMin > nowMin;
      const pName = nextPrayer.prayer.charAt(0).toUpperCase() + nextPrayer.prayer.slice(1);
      visible = [{
        prayer: nextPrayer.prayer,
        status: isUpcoming ? 'upcoming' : 'can_pray_solo_at_mosque',
        status_label: isUpcoming ? 'Upcoming' : `${pName} time`,
        message: isUpcoming
          ? `${pName} at ${nextPrayer.adhan_time}`
          : `${pName} is active — pray at mosque`,
        adhan_time: nextPrayer.adhan_time,
        iqama_time: nextPrayer.iqama_time ?? null,
        urgency: 'low' as const,
        arrival_time: null,
        minutes_until_iqama: null,
        leave_by: null,
        period_ends_at: null,
      }];
    } else {
      return null; // all remaining prayers today are prayed
    }
  }

  // Separate into active (current prayer window) and upcoming
  const active   = visible.find(p => ACTIVE_STATUSES.has(p.status)) ?? null;
  const upcoming = visible.find(p => p.status === 'upcoming') ?? null;

  // Determine display order: show most actionable prayer first
  // If past the switch point for the active prayer, next prayer is more urgent → show it first
  const showNextFirst = active !== null && upcoming !== null && isPastSwitchPoint(active);
  const primary   = showNextFirst ? upcoming! : (active ?? upcoming!);
  const secondary = showNextFirst ? active    : upcoming;

  const cfg = STATUS_CONFIG[primary.status] ?? STATUS_CONFIG['upcoming'];

  function handleClick() {
    setSelectedMosqueId(mosque.id);
    setSingleMosqueNav(mosque);
    setBottomSheetHeight('peek'); // collapse sheet so map + route are visible
  }

  return (
    <div
      className={`rounded-2xl border shadow-sm hover:shadow-md active:scale-[0.99] transition-all cursor-pointer overflow-hidden ${cfg.border}`}
      onClick={handleClick}
    >
      {/* Header */}
      <div className={`flex items-start justify-between gap-2 p-3 pb-2 ${cfg.bg}`}>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-gray-900 text-sm leading-snug truncate">{mosque.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {distLabel(mosque.distance_meters)}
            {mosque.travel_time_minutes ? ` · ${mosque.travel_time_minutes} min` : ''}
          </p>
        </div>
        <img src={cfg.icon} alt="" className="w-10 h-10 flex-shrink-0 object-contain mt-0.5" />
      </div>

      {/* Primary row — bold, colored, with left accent border */}
      <div className={`px-3 py-2 border-l-4 ${cfg.bg} ${cfg.border}`}>
        <p className={`text-sm font-semibold ${cfg.text} leading-snug`}>{primary.message}</p>
        {primary.leave_by && (
          <p className={`text-xs mt-0.5 ${cfg.text} opacity-80`}>Leave by {fmtTime(primary.leave_by)}</p>
        )}
      </div>

      {/* Secondary row — muted, smaller, no left border */}
      {secondary && (
        <div className="px-3 py-1.5 bg-slate-50 flex items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            {secondary.status === 'upcoming'
              ? `${secondary.prayer.charAt(0).toUpperCase() + secondary.prayer.slice(1)} at ${fmtTime(secondary.adhan_time)}`
              : secondary.message}
          </p>
        </div>
      )}

      {/* Combination options (Musafir mode, no route) — hide pairs where both prayers are prayed */}
      {travelMode && mosque.travel_combinations.length > 0 && (() => {
        const PAIR_PRAYERS: Record<string, [string, string]> = {
          dhuhr_asr: ['dhuhr', 'asr'],
          maghrib_isha: ['maghrib', 'isha'],
        };
        const visiblePairs = mosque.travel_combinations.filter((pair: TravelPairPlan) => {
          const prayers = PAIR_PRAYERS[pair.pair];
          if (!prayers) return true;
          return !(effectivePrayed.has(prayers[0]) && effectivePrayed.has(prayers[1]));
        });
        if (visiblePairs.length === 0) return null;
        return (
          <div className={`px-3 pt-2 pb-3 border-t space-y-2 ${th.bgLight} ${th.border}`}>
            {visiblePairs.map((pair: TravelPairPlan) => {
              const taqdeem = pair.options.find((o: TravelOption) => o.option_type === 'combine_early');
              const takheer = pair.options.find((o: TravelOption) => o.option_type === 'combine_late');
              const takheerOnly = !taqdeem && !!takheer;
              return (
                <div key={pair.pair}>
                  <p className={`text-xs font-semibold mb-1.5 ${th.text}`}>
                    {pair.label} — Musafir
                  </p>

                  {/* Ta'kheer only: p1 time passed but NOT missed */}
                  {takheerOnly && (
                    <div className="bg-white rounded-lg border border-blue-200 px-2.5 py-2 space-y-1">
                      <p className="text-xs font-semibold text-blue-800">{pair.label.split(' + ')[0]} is not missed ✓</p>
                      <p className="text-xs text-blue-700">{takheer!.description}</p>
                      <span className="inline-block text-xs bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-full">
                        Jam' Ta'kheer — Combine Late
                      </span>
                    </div>
                  )}

                  {/* Taqdeem — primary when both available or only Taqdeem */}
                  {taqdeem && (
                    <div className={`bg-white rounded-lg border border-green-200 px-2.5 py-2 space-y-1 ${takheer ? 'mb-1.5' : ''}`}>
                      <p className="text-xs text-green-800">{taqdeem.description}</p>
                      <span className="inline-block text-xs bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded-full">
                        Jam' Taqdeem — Combine Early
                      </span>
                    </div>
                  )}

                  {/* Ta'kheer as secondary when Taqdeem also available */}
                  {taqdeem && takheer && (
                    <div className="bg-white rounded-lg border border-blue-100 px-2.5 py-2 space-y-1">
                      <p className="text-xs text-blue-700">{takheer.description}</p>
                      <span className="inline-block text-xs bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded-full">
                        Jam' Ta'kheer — Combine Late
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Source badge */}
      <div className={`px-3 py-1.5 border-t ${cfg.border} bg-white`}>
        <p className={`text-xs ${badge.color}`} title={badge.title}>{badge.label}</p>
      </div>
    </div>
  );
}

// ─── Prayer Spot Card ────────────────────────────────────────────────────────

function SpotCard({ spot }: { spot: PrayerSpot }) {
  const openSheet       = useStore((s) => s.openSheet);
  const confirmedSpots  = useStore((s) => s.confirmedSpots);
  const addConfirmedSpot = useStore((s) => s.addConfirmedSpot);
  const th              = useTheme();
  const [confirming, setConfirming] = useState(false);

  const isConfirmed = confirmedSpots.has(spot.id);

  const handleConfirm = async (e: React.MouseEvent) => {
    e.stopPropagation(); // don't open the detail sheet
    if (isConfirmed || confirming) return;
    setConfirming(true);
    try {
      await apiService.verifySpot(spot.id, {
        session_id: SESSION_ID,
        is_positive: true,
        attributes: {},
      });
      addConfirmedSpot(spot.id);
    } catch {
      // 409 = already voted — mark confirmed anyway so the button updates
      addConfirmedSpot(spot.id);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div
      className={`rounded-xl border bg-white p-3 cursor-pointer hover:shadow-md active:scale-[0.99] transition-all ${th.border}`}
      onClick={() => openSheet({ type: 'spot_detail', spot })}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 text-sm leading-tight truncate">{spot.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {SPOT_TYPE_LABELS[spot.spot_type] ?? spot.spot_type} · {distLabel(spot.distance_meters)}
          </p>
        </div>
        <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${th.bgLight} ${th.text}`}>
          {spot.verification_label}
        </span>
      </div>
      <div className="flex items-center justify-between mt-2 gap-2">
        <div className="flex gap-2 flex-wrap">
          {spot.has_wudu_facilities === true && <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">Wudu</span>}
          {spot.is_indoor === true && <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">Indoor</span>}
          {spot.gender_access === 'men_only' && <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">Men only</span>}
          {spot.gender_access === 'women_only' && <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">Women only</span>}
        </div>
        <button
          onClick={handleConfirm}
          disabled={isConfirmed || confirming}
          className={`flex-shrink-0 text-xs px-2.5 py-1 rounded-full font-medium border transition-colors ${
            isConfirmed
              ? `${th.bgLight} ${th.border} ${th.text} cursor-default`
              : `bg-white ${th.borderStrong} ${th.text} ${th.bgHoverLight}`
          }`}
        >
          {isConfirmed ? '✓ Confirmed' : confirming ? '…' : '✓ I prayed here'}
        </button>
      </div>
    </div>
  );
}

// ─── Mosque Detail Sheet ────────────────────────────────────────────────────

// ─── Mosque Community Suggestions ────────────────────────────────────────────

const IQAMA_FIELDS = ['fajr_iqama', 'dhuhr_iqama', 'asr_iqama', 'maghrib_iqama', 'isha_iqama'];
const FACILITY_FIELDS = ['phone', 'website', 'has_womens_section', 'has_parking', 'wheelchair_accessible'];

function MosqueSuggestions({ mosqueId, mosque }: { mosqueId: string; mosque: Mosque }) {
  const th = useTheme();
  const [suggestions, setSuggestions] = useState<MosqueSuggestion[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [fieldName, setFieldName] = useState('');
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [votedIds, setVotedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    apiService.getMosqueSuggestions(mosqueId)
      .then(r => setSuggestions(r.suggestions))
      .catch(() => {});
  }, [mosqueId]);

  async function handleSubmit() {
    if (!fieldName || !value.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await apiService.submitMosqueSuggestion(mosqueId, fieldName, value.trim(), SESSION_ID);
      setSuggestions(prev => [res, ...prev]);
      setSuccess('Suggestion submitted. Others nearby can confirm it.');
      setShowForm(false);
      setFieldName('');
      setValue('');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to submit suggestion.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVote(suggestionId: string, isPositive: boolean) {
    try {
      const res = await apiService.voteMosqueSuggestion(suggestionId, SESSION_ID, isPositive);
      setSuggestions(prev => prev.map(s =>
        s.id === suggestionId
          ? { ...s, upvote_count: res.upvote_count, downvote_count: res.downvote_count, status: res.status }
          : s
      ).filter(s => s.status === 'pending'));
      setVotedIds(prev => new Set(prev).add(suggestionId));
    } catch {}
  }

  // Pre-fill value field based on selected field
  function onFieldSelect(fn: string) {
    setFieldName(fn);
    setValue('');
    // Pre-fill with current value if it exists
    if (IQAMA_FIELDS.includes(fn)) {
      const prayer = fn.replace('_iqama', '');
      const pt = mosque.prayers.find(p => p.prayer === prayer);
      if (pt?.iqama_time) setValue(pt.iqama_time);
    } else if (fn === 'phone' && mosque.phone) {
      setValue(mosque.phone);
    } else if (fn === 'website' && mosque.website) {
      setValue(mosque.website);
    } else if (fn === 'has_womens_section') {
      setValue(mosque.has_womens_section ? 'true' : 'false');
    } else if (fn === 'wheelchair_accessible') {
      setValue(mosque.wheelchair_accessible ? 'true' : 'false');
    }
  }

  const pendingSuggestions = suggestions.filter(s => s.status === 'pending' && s.submitted_by_session !== SESSION_ID);

  return (
    <div className="mb-4">
      {/* Pending suggestions from others — show as verification cards */}
      {pendingSuggestions.length > 0 && (
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Pending corrections</h3>
          <div className="space-y-2">
            {pendingSuggestions.map(s => (
              <div key={s.id} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-amber-800">
                    {SUGGESTION_FIELD_LABELS[s.field_name] || s.field_name}
                  </span>
                  <span className="text-xs text-amber-600">
                    {s.upvote_count} confirm{s.upvote_count !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm mb-2">
                  {s.current_value && (
                    <>
                      <span className="text-gray-500 line-through">
                        {IQAMA_FIELDS.includes(s.field_name) ? fmtTime(s.current_value) : s.current_value}
                      </span>
                      <span className="text-gray-400">→</span>
                    </>
                  )}
                  <span className="font-medium text-amber-900">
                    {IQAMA_FIELDS.includes(s.field_name) ? fmtTime(s.suggested_value) : s.suggested_value}
                  </span>
                </div>
                {votedIds.has(s.id) ? (
                  <p className="text-xs text-gray-500 text-center py-1">Thanks for your feedback</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleVote(s.id, true)}
                      className={`${th.bg} ${th.bgHover} text-white py-1.5 rounded-lg text-xs font-medium`}
                    >Confirm</button>
                    <button
                      onClick={() => handleVote(s.id, false)}
                      className="bg-slate-100 text-slate-600 border border-slate-200 py-1.5 rounded-lg text-xs font-medium hover:bg-slate-200"
                    >That's wrong</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success message */}
      {success && (
        <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">{success}</p>
      )}

      {/* Suggest a correction link / form */}
      {!showForm ? (
        <button
          onClick={() => { setShowForm(true); setSuccess(''); setError(''); }}
          className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2"
        >
          Times look wrong? Suggest a correction
        </button>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700">Suggest a correction</h4>
            <button onClick={() => { setShowForm(false); setError(''); }} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
          </div>

          {/* Field selector */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">What needs correcting?</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-current bg-white"
              value={fieldName}
              onChange={e => onFieldSelect(e.target.value)}
            >
              <option value="">Select a field</option>
              <optgroup label="Prayer times">
                {IQAMA_FIELDS.map(f => (
                  <option key={f} value={f}>{SUGGESTION_FIELD_LABELS[f]}</option>
                ))}
              </optgroup>
              <optgroup label="Mosque info">
                {FACILITY_FIELDS.map(f => (
                  <option key={f} value={f}>{SUGGESTION_FIELD_LABELS[f]}</option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* Value input — type depends on field */}
          {fieldName && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {IQAMA_FIELDS.includes(fieldName) ? 'Correct time (HH:MM, 24h)' : 'Correct value'}
              </label>
              {['has_womens_section', 'has_parking', 'wheelchair_accessible'].includes(fieldName) ? (
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-current bg-white"
                  value={value}
                  onChange={e => setValue(e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : (
                <input
                  type={IQAMA_FIELDS.includes(fieldName) ? 'time' : 'text'}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-current"
                  placeholder={IQAMA_FIELDS.includes(fieldName) ? '14:30' : ''}
                  value={value}
                  onChange={e => setValue(e.target.value)}
                />
              )}
            </div>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}

          <button
            onClick={handleSubmit}
            disabled={submitting || !fieldName || !value.trim()}
            className={`w-full ${th.bg} ${th.bgHover} text-white py-2 rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {submitting ? 'Submitting…' : 'Submit Suggestion'}
          </button>
          <p className="text-xs text-gray-400 text-center">
            Your suggestion goes live once confirmed by {IQAMA_FIELDS.includes(fieldName) ? '2' : '3'} other users nearby.
          </p>
        </div>
      )}
    </div>
  );
}

function MosqueDetailSheet({ mosque, onDismiss }: { mosque: Mosque; onDismiss?: () => void }) {
  const closeSheet    = useStore((s) => s.closeSheet);
  const prayedToday   = useStore((s) => s.prayedToday);
  const togglePrayed  = useStore((s) => s.togglePrayed);
  const userLocation  = useStore((s) => s.userLocation);
  const th            = useTheme();
  const [navSheetOpen, setNavSheetOpen] = useState(false);

  const destLat  = mosque.location.latitude;
  const destLng  = mosque.location.longitude;
  const destName = mosque.location.address ? `${mosque.name}, ${mosque.location.address}` : mosque.name;
  const navPoints: MapPoint[] = [
    { lat: 0, lng: 0, is_gps: true },
    { lat: destLat, lng: destLng, name: destName },
  ];
  const mosqueGoogleUrl = buildGoogleMapsUrl(navPoints);
  const mosqueAppleUrl  = buildAppleMapsUrl(navPoints);

  async function handleMosqueShare() {
    if (navigator.share) {
      try { await navigator.share({ title: `Navigate to ${mosque.location.address || mosque.name}`, url: mosqueGoogleUrl }); return; }
      catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(mosqueGoogleUrl); }
    catch { window.open(mosqueGoogleUrl, '_blank'); }
  }

  const nc = mosque.next_catchable;
  const isMissed    = nc?.status === 'missed_make_up';
  const isUpcoming  = nc?.status === 'upcoming';
  const isNcPrayed  = nc ? prayedToday.has(nc.prayer) : false;

  // When nc is already prayed, find the next future prayer from the table
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const nextFromTable = isNcPrayed
    ? (mosque.prayers ?? []).find(p => {
        if (!p.adhan_time) return false;
        const [h, m] = p.adhan_time.split(':').map(Number);
        return h * 60 + m > nowMin;
      })
    : null;

  const badge = dataSourceBadge(mosque.prayers);

  // Badge config: upcoming gets theme color (distinct from gray missed)
  const cfg = nc && !isNcPrayed
    ? (isUpcoming
        ? { bg: th.bgLight, border: th.border, text: th.textDark, icon: STATUS_CONFIG['upcoming'].icon }
        : (STATUS_CONFIG[nc.status] ?? STATUS_CONFIG['upcoming']))
    : null;

  return (
    <div className="-mt-1">
      {/* Row 1: mosque name on left, navigate + website + ✕ on right */}
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-base font-bold text-gray-900 flex-1 pr-3 leading-tight truncate">{mosque.name}</h2>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* P0-1: Call button — shown when mosque has phone */}
          {mosque.phone && (
            <a
              href={`tel:${mosque.phone}`}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 active:scale-95 transition-all"
              aria-label="Call mosque"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            </a>
          )}
          {mosque.website && (
            <a
              href={mosque.website}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 active:scale-95 transition-all"
              aria-label="Website"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </a>
          )}
          <button
            onClick={() => setNavSheetOpen(true)}
            className={`w-9 h-9 flex items-center justify-center rounded-full ${th.bg} ${th.bgHover} text-white active:scale-95 transition-all`}
            aria-label="Navigate"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21.71 11.29l-9-9c-.39-.39-1.02-.39-1.41 0l-9 9c-.39.39-.39 1.02 0 1.41l9 9c.39.39 1.02.39 1.41 0l9-9c.39-.38.39-1.01 0-1.41zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z"/>
            </svg>
          </button>
          <button
            onClick={onDismiss ?? closeSheet}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 active:scale-95 transition-all"
            aria-label="Close"
          >✕</button>
        </div>
      </div>

      {/* Row 2: address + denomination */}
      {mosque.location.address && (
        <p className="text-sm text-gray-500 leading-snug">{mosque.location.address}</p>
      )}
      {mosque.denomination && (
        <p className="text-xs text-gray-400 capitalize">{mosque.denomination}</p>
      )}
      {(mosque.location.address || mosque.denomination) && <div className="mb-3" />}

      {/* Status badge — hidden when nc prayer is already marked as prayed */}
      {nc && cfg && (
        <div className={`rounded-lg border px-3 py-2.5 mb-4 ${cfg.bg} ${cfg.border}`}>
          <div className={`flex items-center gap-2 text-sm font-semibold ${cfg.text}`}>
            <img src={cfg.icon} alt="" className="w-10 h-10 object-contain flex-shrink-0" />
            <span className="capitalize">{isUpcoming ? `Next: ${nc.prayer}` : nc.status_label}</span>
          </div>

          {/* Upcoming: structured time display */}
          {isUpcoming ? (
            <div className={`mt-1.5 space-y-0.5 text-sm ${cfg.text}`}>
              <p>Azan at <span className="font-semibold">{fmtTime(nc.adhan_time)}</span>
                {nc.iqama_time && <span className="text-xs font-normal opacity-75"> · Iqama {fmtTime(nc.iqama_time)}</span>}
              </p>
              {nc.leave_by && (
                <p>Leave by <span className="font-semibold">{fmtTime(nc.leave_by)}</span> to pray with Imam</p>
              )}
            </div>
          ) : (
            <>
              <p className={`text-sm mt-0.5 ${cfg.text}`}>{nc.message}</p>
              {nc.iqama_time && !isMissed && (
                <p className="text-xs text-gray-500 mt-1">Iqama: {fmtTime(nc.iqama_time)}</p>
              )}
            </>
          )}

          {/* Mark as prayed button — only for missed prayers */}
          {isMissed && (
            <button
              onClick={() => togglePrayed(nc.prayer)}
              className="mt-2 text-xs px-3 py-1 rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 active:bg-gray-100"
            >
              ✓ I already prayed {nc.prayer.charAt(0).toUpperCase() + nc.prayer.slice(1)}
            </button>
          )}
        </div>
      )}

      {/* Show next prayer when nc is already marked as prayed */}
      {isNcPrayed && nextFromTable && (
        <div className={`rounded-lg border px-3 py-2.5 mb-4 ${th.border} ${th.bgLight}`}>
          <p className={`text-sm font-semibold capitalize ${th.textDark}`}>Next: {nextFromTable.prayer}</p>
          <div className={`mt-1 space-y-0.5 text-sm ${th.text}`}>
            <p>Azan at <span className="font-semibold">{fmtTime(nextFromTable.adhan_time)}</span>
              {nextFromTable.iqama_time && <span className="text-xs font-normal opacity-75"> · Iqama {fmtTime(nextFromTable.iqama_time)}</span>}
            </p>
            {nextFromTable.iqama_time && mosque.travel_time_minutes && (() => {
              const [h, m] = nextFromTable.iqama_time!.split(':').map(Number);
              const leaveMin = h * 60 + m - mosque.travel_time_minutes;
              const lh = Math.floor(((leaveMin % 1440) + 1440) % 1440 / 60);
              const lm = ((leaveMin % 1440) + 1440) % 1440 % 60;
              const suffix = lh >= 12 ? 'PM' : 'AM';
              const fh = lh > 12 ? lh - 12 : lh === 0 ? 12 : lh;
              return <p>Leave by <span className="font-semibold">{fh}:{String(lm).padStart(2,'0')} {suffix}</span> to pray with Imam</p>;
            })()}
          </div>
        </div>
      )}

      {/* Prayer times table */}
      {mosque.prayers.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Today's Prayer Times</h3>
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-3 py-2 text-gray-600 font-medium">Prayer</th>
                  <th className="text-right px-3 py-2 text-gray-600 font-medium">Adhan</th>
                  <th className="text-right px-3 py-2 text-gray-600 font-medium">Iqama</th>
                </tr>
              </thead>
              <tbody>
                {mosque.prayers.map((p: PrayerTime, i) => (
                  <React.Fragment key={i}>
                    <tr className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-800 capitalize">{p.prayer}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{fmtTime(p.adhan_time)}</td>
                      <td className="px-3 py-2 text-right text-gray-700 font-medium">{fmtTime(p.iqama_time)}</td>
                    </tr>
                    {/* Shorooq (sunrise) row after Fajr */}
                    {p.prayer === 'fajr' && mosque.sunrise && (
                      <tr className="border-t border-gray-100 bg-amber-50">
                        <td className="px-3 py-2 text-amber-700 font-medium">Shorooq</td>
                        <td className="px-3 py-2 text-right text-amber-600">{fmtTime(mosque.sunrise)}</td>
                        <td className="px-3 py-2 text-right text-gray-400 text-xs italic">Fajr ends</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Eid prayers — prominent card */}
      {(mosque.special_prayers ?? []).filter((sp: SpecialPrayer) =>
        sp.prayer_type === 'eid_fitr' || sp.prayer_type === 'eid_adha'
      ).length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            {(mosque.special_prayers ?? []).find((sp: SpecialPrayer) => sp.prayer_type === 'eid_fitr')
              ? 'Eid ul-Fitr Prayer'
              : 'Eid ul-Adha Prayer'}
          </h3>
          <div className="space-y-2">
            {(mosque.special_prayers ?? [])
              .filter((sp: SpecialPrayer) => sp.prayer_type === 'eid_fitr' || sp.prayer_type === 'eid_adha')
              .map((sp: SpecialPrayer) => (
                <div key={sp.session_number} className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-800">
                      {sp.session_number > 1 ? `Session ${sp.session_number}` : 'Prayer'}
                    </span>
                    <div className="text-right text-sm text-green-800">
                      {sp.takbeer_time && <span>Takbeer {fmtTime(sp.takbeer_time)}</span>}
                      {sp.takbeer_time && sp.prayer_time && <span className="mx-1">·</span>}
                      {sp.prayer_time && <span className="font-medium">Prayer {fmtTime(sp.prayer_time)}</span>}
                    </div>
                  </div>
                  {sp.imam_name && <p className="text-xs text-green-700 mt-0.5">{sp.imam_name}</p>}
                  {sp.location_notes && <p className="text-xs text-green-700 mt-0.5">{sp.location_notes}</p>}
                  {sp.special_notes && <p className="text-xs text-gray-600 mt-1 italic">{sp.special_notes}</p>}
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Taraweeh — shown during Ramadan */}
      {(mosque.special_prayers ?? []).filter((sp: SpecialPrayer) => sp.prayer_type === 'taraweeh').length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Taraweeh</h3>
          {(mosque.special_prayers ?? [])
            .filter((sp: SpecialPrayer) => sp.prayer_type === 'taraweeh')
            .map((sp: SpecialPrayer, i: number) => (
              <div key={i} className={`rounded-lg border px-3 py-2 ${th.bgLight} ${th.border}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${th.textDark}`}>Taraweeh Nightly</span>
                  {sp.prayer_time && (
                    <span className={`text-sm font-medium ${th.textDark}`}>{fmtTime(sp.prayer_time)}</span>
                  )}
                </div>
                {sp.imam_name && <p className={`text-xs ${th.text} mt-0.5`}>{sp.imam_name}</p>}
              </div>
            ))}
        </div>
      )}

      {/* Friday Jumu'ah sessions */}
      {mosque.jumuah_sessions.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Friday Jumu'ah</h3>
          <div className="space-y-2">
            {mosque.jumuah_sessions.map((s: JumuahSession) => (
              <div key={s.session_number} className={`rounded-lg border px-3 py-2 ${th.bgLight} ${th.border}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${th.textDark}`}>
                    {mosque.jumuah_sessions.length > 1 ? `Session ${s.session_number}` : 'Jumu\'ah Prayer'}
                  </span>
                  <div className={`text-right text-sm ${th.textDark}`}>
                    {s.khutba_start && <span>Khutba {fmtTime(s.khutba_start)}</span>}
                    {s.khutba_start && s.prayer_start && <span className="mx-1">·</span>}
                    {s.prayer_start && <span className="font-medium">Prayer {fmtTime(s.prayer_start)}</span>}
                  </div>
                </div>
                {s.imam_name && (
                  <p className={`text-xs ${th.text} mt-0.5`}>{s.imam_name}</p>
                )}
                {s.language && s.language.toLowerCase() !== 'english' && (
                  <p className={`text-xs ${th.text} mt-0.5`}>Language: {s.language}</p>
                )}
                {s.special_notes && (
                  <p className="text-xs text-gray-600 mt-1 italic">{s.special_notes}</p>
                )}
                {s.booking_required && (
                  <p className="text-xs text-amber-700 mt-1">
                    Registration required
                    {s.booking_url && (
                      <a href={s.booking_url} target="_blank" rel="noopener noreferrer" className="ml-1 underline">
                        Register
                      </a>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Community suggestions */}
      <MosqueSuggestions mosqueId={mosque.id} mosque={mosque} />

      {/* Data source */}
      <p className={`text-xs mb-4 ${badge.color}`} title={badge.title}>{badge.label}</p>

      {/* Navigate action sheet — portal to document.body to escape stacking context */}
      {navSheetOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[900] flex items-end" onClick={() => setNavSheetOpen(false)}>
          <div className="w-full bg-white rounded-t-2xl shadow-2xl pb-8 px-4 pt-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center mb-4"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Open in…</p>
            <div className="space-y-2">
              <button onClick={() => { window.open(mosqueGoogleUrl, '_blank'); setNavSheetOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-gray-50 active:bg-gray-100 text-sm font-medium text-gray-800">
                <img src="https://www.google.com/favicon.ico" alt="" className="w-5 h-5 rounded" />
                Google Maps
              </button>
              {IS_IOS && (
                <button onClick={() => { window.open(mosqueAppleUrl, '_blank'); setNavSheetOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-gray-50 active:bg-gray-100 text-sm font-medium text-gray-800">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 814 1000" fill="currentColor">
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.8-155.5-127.5c-43.5-74.2-77.5-188.6-77.5-297.5 0-179 116.7-273.8 231.5-273.8 61.5 0 112.8 40.8 150.7 40.8 36.2 0 93.8-43.4 162.8-43.4 26.2 0 108.2 2.6 168.3 92.8zm-107-99.4C720.8 168 743.1 111.8 743.1 55c0-5.8-.6-11.6-1.9-16.5-57.3 2.6-124.9 38.9-166.2 89.7-36.2 43.4-70.1 113.1-70.1 182.7 0 6.4 1.3 12.8 1.9 15.5 3.9.6 10.2 1.3 16.5 1.3 50.6 0 113.5-33.9 157.8-86.1z"/>
                  </svg>
                  Apple Maps
                </button>
              )}
              <button onClick={async () => { await handleMosqueShare(); setNavSheetOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-gray-50 active:bg-gray-100 text-sm font-medium text-gray-800">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                Share Route
              </button>
            </div>
            <button
              onClick={() => setNavSheetOpen(false)}
              onTouchEnd={(e) => { e.stopPropagation(); setNavSheetOpen(false); }}
              className="w-full mt-3 py-3 text-sm font-semibold text-gray-400 active:text-gray-600"
            >Cancel</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Spot Detail Sheet ───────────────────────────────────────────────────────

function SpotDetailSheet({ spot }: { spot: PrayerSpot }) {
  const closeSheet = useStore((s) => s.closeSheet);
  const th         = useTheme();
  const [submitting, setSubmitting] = useState(false);
  const [voted, setVoted]           = useState<boolean | null>(null);
  const [navSheetOpen, setNavSheetOpen] = useState(false);

  const destLat = spot.location.latitude;
  const destLng = spot.location.longitude;
  const destName = spot.location.address ? `${spot.name}, ${spot.location.address}` : spot.name;
  const navPoints: MapPoint[] = [
    { lat: 0, lng: 0, is_gps: true },
    { lat: destLat, lng: destLng, name: destName },
  ];
  const spotGoogleUrl = buildGoogleMapsUrl(navPoints);
  const spotAppleUrl  = buildAppleMapsUrl(navPoints);

  const handleSpotShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `Navigate to ${destName}`, url: spotGoogleUrl }); return; }
      catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(spotGoogleUrl); }
    catch { window.open(spotGoogleUrl, '_blank'); }
  };

  const handleVerify = async (isPositive: boolean) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await apiService.verifySpot(spot.id, {
        session_id: SESSION_ID,
        is_positive: isPositive,
        attributes: {},
      });
      setVoted(isPositive);
    } catch (e) {
      // silently ignore duplicate vote errors
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Row 1: spot name + ✕ */}
      <div className="flex items-start justify-between mb-2">
        <h2 className="text-lg font-bold text-gray-900 pr-4 leading-tight">{spot.name}</h2>
        <button onClick={closeSheet} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 text-lg flex-shrink-0 transition-colors">✕</button>
      </div>

      {/* Row 2: type + address on left, navigate icon on right */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500 flex-1 pr-3">
          {SPOT_TYPE_LABELS[spot.spot_type] ?? spot.spot_type}
          {spot.location.address ? ` · ${spot.location.address}` : ''}
        </p>
        <button
          onClick={() => setNavSheetOpen(true)}
          className={`w-9 h-9 flex items-center justify-center rounded-full ${th.bg} ${th.bgHover} text-white active:scale-95 transition-all flex-shrink-0`}
          aria-label="Navigate"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21.71 11.29l-9-9c-.39-.39-1.02-.39-1.41 0l-9 9c-.39.39-.39 1.02 0 1.41l9 9c.39.39 1.02.39 1.41 0l9-9c.39-.38.39-1.01 0-1.41zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z"/>
          </svg>
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {spot.has_wudu_facilities === true && (
          <span className={`text-xs px-2 py-1 rounded-full border ${th.bgLight} ${th.border} ${th.text}`}>Wudu facilities</span>
        )}
        {spot.is_indoor === true && (
          <span className="bg-slate-100 border border-slate-200 text-slate-600 text-xs px-2 py-1 rounded-full">Indoor</span>
        )}
        {spot.gender_access === 'men_only' && (
          <span className="bg-slate-100 border border-slate-200 text-slate-600 text-xs px-2 py-1 rounded-full">Men only</span>
        )}
        {spot.gender_access === 'women_only' && (
          <span className="bg-slate-100 border border-slate-200 text-slate-600 text-xs px-2 py-1 rounded-full">Women only</span>
        )}
        {spot.operating_hours && (
          <span className="bg-slate-100 border border-slate-200 text-slate-600 text-xs px-2 py-1 rounded-full">{spot.operating_hours}</span>
        )}
      </div>

      {spot.notes && (
        <p className="text-sm text-gray-600 mb-3 italic">"{spot.notes}"</p>
      )}

      <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm text-gray-600">
        <span className="font-medium">{spot.verification_label}</span>
        {spot.last_verified_at && (
          <span className="ml-2 text-xs text-gray-400">
            · last verified {new Date(spot.last_verified_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {voted !== null ? (
        <p className="text-center text-sm text-gray-600 py-2">
          {voted ? '👍 Thanks for confirming!' : '👎 Thanks for the feedback!'}
        </p>
      ) : (
        <div>
          <p className="text-xs text-gray-500 mb-2 text-center">Has this place a prayer area?</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => handleVerify(true)}
              disabled={submitting}
              className={`${th.bg} ${th.bgHover} text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50`}
            >
              Yes, confirm
            </button>
            <button
              onClick={() => handleVerify(false)}
              disabled={submitting}
              className="bg-slate-100 text-slate-600 border border-slate-200 py-2 rounded-lg text-sm font-medium hover:bg-slate-200 disabled:opacity-50"
            >
              No longer valid
            </button>
          </div>
        </div>
      )}

      {/* Navigate action sheet — portal to document.body */}
      {navSheetOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[900] flex items-end" onClick={() => setNavSheetOpen(false)}>
          <div className="w-full bg-white rounded-t-2xl shadow-2xl pb-8 px-4 pt-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center mb-4"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Open in…</p>
            <div className="space-y-2">
              <button onClick={() => { window.open(spotGoogleUrl, '_blank'); setNavSheetOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-gray-50 active:bg-gray-100 text-sm font-medium text-gray-800">
                <img src="https://www.google.com/favicon.ico" alt="" className="w-5 h-5 rounded" />
                Google Maps
              </button>
              {IS_IOS && (
                <button onClick={() => { window.open(spotAppleUrl, '_blank'); setNavSheetOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-gray-50 active:bg-gray-100 text-sm font-medium text-gray-800">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 814 1000" fill="currentColor">
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.8-155.5-127.5c-43.5-74.2-77.5-188.6-77.5-297.5 0-179 116.7-273.8 231.5-273.8 61.5 0 112.8 40.8 150.7 40.8 36.2 0 93.8-43.4 162.8-43.4 26.2 0 108.2 2.6 168.3 92.8zm-107-99.4C720.8 168 743.1 111.8 743.1 55c0-5.8-.6-11.6-1.9-16.5-57.3 2.6-124.9 38.9-166.2 89.7-36.2 43.4-70.1 113.1-70.1 182.7 0 6.4 1.3 12.8 1.9 15.5 3.9.6 10.2 1.3 16.5 1.3 50.6 0 113.5-33.9 157.8-86.1z"/>
                  </svg>
                  Apple Maps
                </button>
              )}
              <button onClick={async () => { await handleSpotShare(); setNavSheetOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-gray-50 active:bg-gray-100 text-sm font-medium text-gray-800">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                Share Route
              </button>
            </div>
            <button
              onClick={() => setNavSheetOpen(false)}
              onTouchEnd={(e) => { e.stopPropagation(); setNavSheetOpen(false); }}
              className="w-full mt-3 py-3 text-sm font-semibold text-gray-400 active:text-gray-600"
            >Cancel</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Spot Submit Sheet ───────────────────────────────────────────────────────

function SpotSubmitSheet() {
  const closeSheet   = useStore((s) => s.closeSheet);
  const th           = useTheme();
  const userLocation = useStore((s) => s.userLocation);

  // Resolved lat/lng for the spot (starts as GPS, can be overridden by address search)
  const [spotLat, setSpotLat] = useState<number | null>(userLocation?.latitude ?? null);
  const [spotLng, setSpotLng] = useState<number | null>(userLocation?.longitude ?? null);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationSugg, setLocationSugg]   = useState<GeocodeSuggestion[]>([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const locationDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [form, setForm] = useState<Partial<SpotSubmitRequest>>({
    spot_type: 'prayer_room',
    gender_access: 'all',
    is_indoor: true,
    has_wudu_facilities: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]             = useState(false);
  const [error, setError]           = useState<string | null>(null);

  // Pre-fill address input with reverse-geocoded GPS address on open
  useEffect(() => {
    if (!userLocation) return;
    setSpotLat(userLocation.latitude);
    setSpotLng(userLocation.longitude);
    apiService.reverseGeocode(userLocation.latitude, userLocation.longitude)
      .then((lbl) => { if (lbl) setLocationQuery(lbl); })
      .catch(() => {});
  }, [userLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  function onLocationInput(val: string) {
    setLocationQuery(val);
    if (locationDebounce.current) clearTimeout(locationDebounce.current);
    if (val.length < 3) { setLocationSugg([]); return; }
    locationDebounce.current = setTimeout(async () => {
      setLocationLoading(true);
      try { setLocationSugg(await apiService.geocodeDestination(val, userLocation?.latitude, userLocation?.longitude)); }
      catch { setLocationSugg([]); }
      finally { setLocationLoading(false); }
    }, 400);
  }

  const set = (k: keyof SpotSubmitRequest, v: unknown) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name?.trim() || spotLat === null || spotLng === null) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiService.submitSpot({
        name: form.name!,
        spot_type: form.spot_type!,
        latitude: spotLat,
        longitude: spotLng,
        address: form.address,
        has_wudu_facilities: form.has_wudu_facilities ?? null,
        gender_access: form.gender_access,
        is_indoor: form.is_indoor ?? null,
        operating_hours: form.operating_hours,
        notes: form.notes,
        website: form.website,
        session_id: SESSION_ID,
      });
      setDone(true);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6">
        <img src="/icons/icon_mosque_nav.png" alt="" className="w-16 h-16 object-contain mx-auto mb-3" />
        <h3 className="text-lg font-bold text-gray-900 mb-2">Spot Submitted!</h3>
        <p className="text-sm text-gray-600 mb-4">
          Your spot is pending community verification. Once 3 users confirm it, it will become active.
        </p>
        <button onClick={closeSheet} className={`${th.bg} text-white px-6 py-2 rounded-lg font-medium`}>
          Done
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900">Add Prayer Spot</h2>
        <button onClick={closeSheet} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 text-lg flex-shrink-0 transition-colors">✕</button>
      </div>

      <div className="space-y-3">
        {/* Name */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-current"
            placeholder="e.g. Whole Foods Prayer Room"
            value={form.name ?? ''}
            onChange={(e) => set('name', e.target.value)}
          />
        </div>

        {/* Location — GPS or address search */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Location *</label>
          <div className="relative">
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-current"
              placeholder="Search for an address…"
              value={locationQuery}
              onChange={(e) => onLocationInput(e.target.value)}
            />
            {locationLoading && (
              <span className="absolute right-2.5 top-2.5 text-gray-400 text-xs">…</span>
            )}
            {locationSugg.length > 0 && (
              <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                {locationSugg.map((s, i) => (
                  <button
                    key={i}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    onClick={() => {
                      setSpotLat(s.lat);
                      setSpotLng(s.lng);
                      setLocationQuery(s.place_name);
                      setLocationSugg([]);
                    }}
                  >
                    📍 {s.place_name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {spotLat === null && (
            <p className="text-xs text-amber-600 mt-1">Search for an address or allow location access to place this spot.</p>
          )}
        </div>

        {/* Type */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-current"
            value={form.spot_type}
            onChange={(e) => set('spot_type', e.target.value)}
          >
            {Object.entries(SPOT_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        {/* Facilities */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Wudu?</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none"
              value={form.has_wudu_facilities === null ? 'unknown' : form.has_wudu_facilities ? 'yes' : 'no'}
              onChange={(e) => set('has_wudu_facilities', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null)}
            >
              <option value="unknown">Unknown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Indoor?</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none"
              value={form.is_indoor === null ? 'unknown' : form.is_indoor ? 'yes' : 'no'}
              onChange={(e) => set('is_indoor', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null)}
            >
              <option value="unknown">Unknown</option>
              <option value="yes">Yes</option>
              <option value="no">No (outdoor)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Access</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none"
              value={form.gender_access ?? 'all'}
              onChange={(e) => set('gender_access', e.target.value)}
            >
              <option value="all">All</option>
              <option value="men_only">Men</option>
              <option value="women_only">Women</option>
            </select>
          </div>
        </div>

        {/* Hours */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Hours (optional)</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-current"
            placeholder="e.g. Mon–Fri 9am–6pm"
            value={form.operating_hours ?? ''}
            onChange={(e) => set('operating_hours', e.target.value)}
          />
        </div>

        {/* Website */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Website (optional)</label>
          <input
            type="url"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-current"
            placeholder="https://..."
            value={form.website ?? ''}
            onChange={(e) => set('website', e.target.value)}
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
          <textarea
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-current resize-none"
            placeholder="Any helpful details for other musallees..."
            value={form.notes ?? ''}
            onChange={(e) => set('notes', e.target.value)}
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <button
          onClick={handleSubmit}
          disabled={submitting || !form.name?.trim() || spotLat === null}
          className={`w-full ${th.bg} ${th.bgHover} text-white py-2.5 rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {submitting ? 'Submitting…' : 'Submit Spot'}
        </button>
      </div>
    </div>
  );
}

// ─── Settings Sheet ──────────────────────────────────────────────────────────

function SettingsSheet() {
  const closeSheet          = useStore((s) => s.closeSheet);
  const th                  = useTheme();
  const radiusKm            = useStore((s) => s.radiusKm);
  const setRadiusKm         = useStore((s) => s.setRadiusKm);
  const denominationFilter  = useStore((s) => s.denominationFilter);
  const setDenominationFilter = useStore((s) => s.setDenominationFilter);
  const showSpots           = useStore((s) => s.showSpots);
  const setShowSpots        = useStore((s) => s.setShowSpots);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900">Settings</h2>
        <button onClick={closeSheet} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 text-lg flex-shrink-0 transition-colors">✕</button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search radius: <span className={`${th.text} font-semibold`}>
              {USE_METRIC ? `${radiusKm} km` : `${Math.round(radiusKm / 1.60934)} mi`}
            </span>
          </label>
          <input
            type="range" min={1} max={50} step={1}
            value={radiusKm}
            onChange={(e) => setRadiusKm(Number(e.target.value))}
            className="w-full"
            style={{ accentColor: th.hex }}
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            {USE_METRIC
              ? <><span>1 km</span><span>50 km</span></>
              : <><span>1 mi</span><span>31 mi</span></>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Denomination</label>
          <div className="grid grid-cols-2 gap-2">
            {(['all', 'sunni', 'shia', 'ismaili'] as const).map((d) => (
              <button
                key={d}
                onClick={() => setDenominationFilter(d)}
                className={`py-2 rounded-lg text-sm font-medium border transition-colors ${
                  denominationFilter === d
                    ? `${th.bg} text-white ${th.borderStrong}`
                    : `bg-white text-gray-700 border-gray-300 ${th.borderHover}`
                }`}
              >
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Show spots */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">Show prayer spots</p>
            <p className="text-xs text-gray-500">Community-added non-mosque locations</p>
          </div>
          <button
            onClick={() => setShowSpots(!showSpots)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-3 ${
              showSpots ? th.bg : 'bg-gray-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              showSpots ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bottom Sheet ────────────────────────────────────────────────────────────

function BottomSheet() {
  const sheet = useStore((s) => s.bottomSheet);
  if (!sheet) return null;

  return (
    <>
      {/* Backdrop — absorb all touch/pointer events so the map underneath doesn't pan */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-[590]"
        onClick={useStore.getState().closeSheet}
        onTouchMove={(e) => e.preventDefault()}
      />
      {/* Sheet — stop propagation so scroll inside the sheet doesn't reach the map */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-[600] max-h-[85vh] overflow-y-auto"
        onTouchMove={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>
        <div className="px-4 pb-8 pt-1">
          {sheet.type === 'mosque_detail' && <MosqueDetailSheet mosque={sheet.mosque} />}
          {sheet.type === 'spot_detail'   && <SpotDetailSheet   spot={sheet.spot} />}
          {sheet.type === 'spot_submit'   && <SpotSubmitSheet />}
          {sheet.type === 'settings'      && <SettingsSheet />}
        </div>
      </div>
    </>
  );
}

// ─── Global Prayed Banner ────────────────────────────────────────────────────

// Musafir pair mapping: if one of these prayers is active, show the pair instead
const MUSAFIR_PAIR_MAP: Record<string, { p1: string; p2: string; label: string }> = {
  isha:    { p1: 'maghrib', p2: 'isha', label: 'Maghrib + Isha' },
  maghrib: { p1: 'maghrib', p2: 'isha', label: 'Maghrib + Isha' },
  asr:     { p1: 'dhuhr',   p2: 'asr',  label: 'Dhuhr + Asr'   },
  dhuhr:   { p1: 'dhuhr',   p2: 'asr',  label: 'Dhuhr + Asr'   },
};

function PrayedBanner({ mosques }: { mosques: Mosque[] }) {
  const prayedToday      = useStore((s) => s.prayedToday);
  const togglePrayed     = useStore((s) => s.togglePrayed);
  const togglePrayedPair = useStore((s) => s.togglePrayedPair);
  const travelMode       = useStore((s) => s.travelMode);
  const th               = useTheme();

  // In Musafir mode, praying the later prayer implies the earlier one is done too.
  // e.g. if Asr is prayed → Dhuhr is implicitly prayed. If Isha is prayed → Maghrib too.
  const effectivePrayed = new Set(prayedToday);
  if (travelMode) {
    if (effectivePrayed.has('asr'))  effectivePrayed.add('dhuhr');
    if (effectivePrayed.has('isha')) effectivePrayed.add('maghrib');
  }

  // Collect active (non-upcoming) prayers across all mosques
  const activePrayers = new Set<string>();
  for (const mosque of mosques) {
    const catchable = mosque.catchable_prayers ?? (mosque.next_catchable ? [mosque.next_catchable] : []);
    for (const p of catchable) {
      if (ACTIVE_STATUSES.has(p.status)) activePrayers.add(p.prayer);
    }
  }

  if (activePrayers.size === 0) return null;

  // In Musafir mode, group Dhuhr+Asr and Maghrib+Isha into pairs
  type BannerItem =
    | { kind: 'solo';  prayer: string }
    | { kind: 'pair';  p1: string; p2: string; label: string };

  const items: BannerItem[] = [];
  const seen = new Set<string>();

  for (const prayer of Array.from(activePrayers)) {
    if (seen.has(prayer)) continue;
    if (travelMode && MUSAFIR_PAIR_MAP[prayer]) {
      const { p1, p2, label } = MUSAFIR_PAIR_MAP[prayer];
      // Show as a pair — either both prayed (with undo) or both unprayed
      items.push({ kind: 'pair', p1, p2, label });
      seen.add(p1); seen.add(p2);
    } else {
      items.push({ kind: 'solo', prayer });
      seen.add(prayer);
    }
  }

  return (
    <div className="space-y-1.5">
      {items.map((item) => {
        if (item.kind === 'pair') {
          // In Musafir mode, praying the later prayer implies the earlier is done
          const prayed = effectivePrayed.has(item.p1) && effectivePrayed.has(item.p2);
          return (
            <div
              key={item.label}
              className={`flex items-center justify-between rounded-xl px-3 py-2 border text-sm ${
                prayed ? `${th.bgLight} ${th.border} ${th.textDark}` : 'bg-white border-gray-200 text-gray-700'
              }`}
            >
              <span className="flex items-center gap-1.5">
                {prayed && <img src="/icons/icon_prayed.png" alt="" className="w-7 h-7 object-contain" />}
                {prayed
                  ? `Already prayed ${item.label} today`
                  : `${item.label} — did you already pray both?`}
              </span>
              <button
                onClick={() => togglePrayedPair(item.p1, item.p2)}
                className={`ml-3 text-xs font-medium px-2 py-1 rounded-full border transition-colors ${
                  prayed
                    ? `${th.border} ${th.text} ${th.bgHoverLight}`
                    : `${th.bg} ${th.borderStrong} text-white ${th.bgHover}`
                }`}
              >
                {prayed ? 'Undo' : 'Yes, I prayed'}
              </button>
            </div>
          );
        }
        const prayed = prayedToday.has(item.prayer);
        return (
          <div
            key={item.prayer}
            className={`flex items-center justify-between rounded-xl px-3 py-2 border text-sm ${
              prayed ? `${th.bgLight} ${th.border} ${th.textDark}` : 'bg-white border-gray-200 text-gray-700'
            }`}
          >
            <span className="flex items-center gap-1.5">
              {prayed && <img src="/icons/icon_prayed.png" alt="" className="w-7 h-7 object-contain" />}
              {prayed
                ? `Already prayed ${item.prayer.charAt(0).toUpperCase() + item.prayer.slice(1)} today`
                : `${item.prayer.charAt(0).toUpperCase() + item.prayer.slice(1)} time — did you already pray?`}
            </span>
            <button
              onClick={() => togglePrayed(item.prayer)}
              className={`ml-3 text-xs font-medium px-2 py-1 rounded-full border transition-colors ${
                prayed
                  ? `${th.border} ${th.text} ${th.bgHoverLight}`
                  : `${th.bg} ${th.borderStrong} text-white ${th.bgHover}`
              }`}
            >
              {prayed ? 'Undo' : 'Yes, I prayed'}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─── Last Resort Card ────────────────────────────────────────────────────────

function LastResortCard() {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
      <p className="text-sm font-semibold text-gray-700 mb-1">No nearby spots?</p>
      <p className="text-xs text-gray-500">
        Look for a quiet corner in a parking lot, a gas station, or any clean outdoor area.
        Face the qibla and make tayammum if water isn't available.
      </p>
    </div>
  );
}

// ─── Settings Button ─────────────────────────────────────────────────────────

function SettingsButton() {
  const openSheet = useStore((s) => s.openSheet);
  return (
    <button
      onClick={() => openSheet({ type: 'settings' })}
      className="h-12 px-3 flex items-center justify-center bg-white/95 backdrop-blur-sm rounded-xl shadow-lg flex-shrink-0"
      aria-label="Settings"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.923-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" clipRule="evenodd"/>
      </svg>
    </button>
  );
}

// ─── Destination Input ───────────────────────────────────────────────────────

function GeoInput({
  placeholder, icon = '', value, onChange, suggestions, onSelect, loading, onClear,
}: {
  placeholder: string; icon?: string; value: string;
  onChange: (v: string) => void;
  suggestions: GeocodeSuggestion[]; onSelect: (s: GeocodeSuggestion) => void;
  loading: boolean;
  onClear?: () => void;
}) {
  const th = useTheme();
  return (
    <div className="relative">
      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2.5 py-2">
        {icon && <span className="text-xs font-medium text-gray-400 flex-shrink-0 min-w-[28px]">{icon}</span>}
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 outline-none bg-transparent text-gray-800 placeholder-gray-400 min-w-0"
          style={{ fontSize: 16 }}
        />
        {loading && <span className="text-xs text-gray-300">…</span>}
        {onClear && !loading && (
          <button
            onClick={onClear}
            className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 text-xs leading-none"
            title="Clear — use current location"
          >
            ×
          </button>
        )}
      </div>
      {suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden">
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => onSelect(s)}
              className={`w-full text-left px-4 py-3 text-sm text-gray-800 border-b border-gray-50 last:border-0 truncate hover:bg-gray-50`}>
              {s.place_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Waypoint row state (local — not persisted between form open/close)
let _wpIdCounter = 0;
interface WaypointRow {
  id: number;  // stable key for React reconciliation — survives reordering
  dest: TravelDestination | null;
  query: string;
  sugg: GeocodeSuggestion[];
  loading: boolean;
}

function DestinationInput() {
  const th              = useTheme();
  const userLocation    = useStore((s) => s.userLocation);
  const travelDestination = useStore((s) => s.travelDestination);
  const setTravelDestination = useStore((s) => s.setTravelDestination);
  const travelOrigin    = useStore((s) => s.travelOrigin);
  const setTravelOrigin = useStore((s) => s.setTravelOrigin);
  const travelDepartureTime = useStore((s) => s.travelDepartureTime);
  const setTravelDepartureTime = useStore((s) => s.setTravelDepartureTime);
  const travelPlan      = useStore((s) => s.travelPlan);
  const setTravelPlan        = useStore((s) => s.setTravelPlan);
  const travelPlanLoading    = useStore((s) => s.travelPlanLoading);
  const setTravelPlanLoading = useStore((s) => s.setTravelPlanLoading);
  const travelModeStore = useStore((s) => s.travelMode);
  const prayedToday     = useStore((s) => s.prayedToday);
  const setTripPlannerOpen = useStore((s) => s.setTripPlannerOpen);
  const setTripWaypoints   = useStore((s) => s.setTripWaypoints);

  const [destQuery, setDestQuery]   = useState(travelDestination?.place_name ?? '');
  const [destSugg, setDestSugg]     = useState<GeocodeSuggestion[]>([]);
  const [destLoading, setDestLoading] = useState(false);
  const [originQuery, setOriginQuery] = useState(travelOrigin?.place_name ?? '');
  const [originSugg, setOriginSugg]  = useState<GeocodeSuggestion[]>([]);
  const [originLoading, setOriginLoading] = useState(false);
  // Trip mode always follows global Muqeem/Musafir toggle — no per-trip override
  const tripMode = travelModeStore ? 'travel' : 'driving';

  // Intermediate waypoint rows (0–4 stops between origin and destination)
  const [waypointRows, setWaypointRows] = useState<WaypointRow[]>([]);
  const wpDebounces = useRef<Array<ReturnType<typeof setTimeout> | null>>([]);
  // Cache of computed plans keyed by trip config + mode; invalidated when route changes
  const planCacheRef = useRef<Map<string, import('./types').TravelPlan>>(new Map());

  // State machine: searchMode = destination autocomplete open, editMode = from/to/stops form open
  const [searchMode, setSearchMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [longTripModal, setLongTripModal] = useState<number | null>(null); // km distance if long trip
  const setTravelPlanError = useStore((s) => s.setTravelPlanError);

  // Default departure time = right now in local time (datetime-local needs YYYY-MM-DDTHH:mm)
  const defaultDeparture = (() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  const [departureInput, setDepartureInput] = useState(defaultDeparture);

  const destDebounce  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Consume any pending destination from the deep-link share handler
  useEffect(() => {
    const pending = sessionStorage.getItem('cap_pending_dest');
    if (pending) {
      sessionStorage.removeItem('cap_pending_dest');
      setDestQuery(pending);
      debounceGeocode(pending, destDebounce, setDestLoading, setDestSugg);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-populate "From" field with current location address.
  // Re-runs when form opens (formExpanded) so "From" repopulates after clearAll().
  useEffect(() => {
    if (!userLocation || travelOrigin || originQuery) return;
    apiService.reverseGeocode(userLocation.latitude, userLocation.longitude)
      .then((label) => { if (label) setOriginQuery(label); })
      .catch(() => {});
  }, [userLocation, editMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-replan when mode changes while a plan is already loaded (use cache when available)
  const prevTripModeRef = useRef(travelModeStore);
  useEffect(() => {
    if (prevTripModeRef.current === travelModeStore) return;
    prevTripModeRef.current = travelModeStore;
    if (travelDestination && !editMode) {
      executePlan(travelModeStore ? 'travel' : 'driving');
    }
  }, [travelModeStore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto re-plan when prayed prayers change while a plan is active
  const prevPrayedRef = useRef<Set<string>>(prayedToday);
  useEffect(() => {
    if (travelPlan && prevPrayedRef.current !== prayedToday && travelDestination) {
      prevPrayedRef.current = prayedToday;
      executePlan(tripMode);
    }
  }, [prayedToday]); // eslint-disable-line react-hooks/exhaustive-deps

  function debounceGeocode(
    val: string,
    ref: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
    setLoading: (v: boolean) => void,
    setSugg: (s: GeocodeSuggestion[]) => void,
  ) {
    if (ref.current) clearTimeout(ref.current);
    if (val.length < 3) { setSugg([]); return; }
    ref.current = setTimeout(async () => {
      setLoading(true);
      try { setSugg(await apiService.geocodeDestination(val, userLocation?.latitude, userLocation?.longitude)); }
      catch { setSugg([]); }
      finally { setLoading(false); }
    }, 400);
  }

  function debounceGeocodeWp(rowId: number, index: number, val: string) {
    if (wpDebounces.current[index]) clearTimeout(wpDebounces.current[index]!);
    // Use rowId (stable) instead of index (changes on reorder) for matching in functional updaters
    setWaypointRows(rows => rows.map(r => r.id === rowId ? { ...r, query: val, sugg: val.length < 3 ? [] : r.sugg } : r));
    if (val.length < 3) return;
    wpDebounces.current[index] = setTimeout(async () => {
      setWaypointRows(rows => rows.map(r => r.id === rowId ? { ...r, loading: true } : r));
      try {
        const sugg = await apiService.geocodeDestination(val, userLocation?.latitude, userLocation?.longitude);
        setWaypointRows(rows => rows.map(r => r.id === rowId ? { ...r, sugg, loading: false } : r));
      } catch {
        setWaypointRows(rows => rows.map(r => r.id === rowId ? { ...r, sugg: [], loading: false } : r));
      }
    }, 400);
  }

  function addWaypoint() {
    if (waypointRows.length >= 4) return;
    setWaypointRows(rows => [...rows, { id: ++_wpIdCounter, dest: null, query: '', sugg: [], loading: false }]);
    wpDebounces.current.push(null);
  }

  function removeWaypoint(index: number) {
    if (wpDebounces.current[index]) clearTimeout(wpDebounces.current[index]!);
    setWaypointRows(rows => rows.filter((_, i) => i !== index));
    wpDebounces.current.splice(index, 1);
  }

  function moveWaypoint(index: number, direction: -1 | 1) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= waypointRows.length) return;
    setWaypointRows(rows => {
      const next = [...rows];
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return next;
    });
    const tmp = wpDebounces.current[index];
    wpDebounces.current[index] = wpDebounces.current[newIndex];
    wpDebounces.current[newIndex] = tmp;
  }

  function clearAll() {
    setTravelDestination(null);
    setTravelOrigin(null);
    setTravelDepartureTime(null);
    setTravelPlan(null);
    setDestQuery(''); setDestSugg([]);
    setOriginQuery(''); setOriginSugg([]);
    setWaypointRows([]);
    wpDebounces.current = [];
    planCacheRef.current.clear();
    setDepartureInput(defaultDeparture);
    setSearchMode(false);
    setEditMode(false);
    setTripWaypoints([]);
    // Immediately hide bottom sheet off-screen state and update CSS var
    useStore.getState().setTripPlannerOpen(false);
    // Reset map to "fresh start" — re-zoom to search radius around current location
    useStore.getState().setSelectedMosqueId(null);
    useStore.getState().setMapFocusCoords(null);
    useStore.getState().setBottomSheetHeight('peek');
    useStore.getState().setSelectedItineraryIndex(null);
    useStore.getState().setSingleMosqueNav(null);
  }

  // Non-null = long-trip warning is showing; value = distance in km
  const [longTripKm, setLongTripKm] = useState<number | null>(null);
  const setTravelMode = useStore((s) => s.setTravelMode);

  // Sync form open state → store (hides bottom sheet while planning)
  useEffect(() => {
    setTripPlannerOpen(searchMode || editMode);
  }, [searchMode, editMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync confirmed waypoints → store (for map pins)
  useEffect(() => {
    setTripWaypoints(waypointRows.filter(w => w.dest !== null).map(w => w.dest!));
  }, [waypointRows]); // eslint-disable-line react-hooks/exhaustive-deps

  function planCacheKey(mode: string, oLat: number, oLng: number, dLat: number, dLng: number, wps: Array<{lat:number;lng:number}>, dep: string | undefined): string {
    return `${mode}|${oLat.toFixed(4)},${oLng.toFixed(4)}|${dLat.toFixed(4)},${dLng.toFixed(4)}|${wps.map(w=>`${w.lat.toFixed(4)},${w.lng.toFixed(4)}`).join('+')}|${dep||''}`;
  }

  async function executePlan(mode: 'travel' | 'driving') {
    if (!travelDestination) return;
    const originLat = travelOrigin?.lat ?? userLocation?.latitude;
    const originLng = travelOrigin?.lng ?? userLocation?.longitude;
    if (!originLat || !originLng) return;

    setLongTripKm(null);
    setTravelPlanError(null);
    const depIso = departureInput ? new Date(departureInput).toISOString() : undefined;
    setTravelDepartureTime(depIso || null);
    setTravelPlanLoading(true);
    setTravelPlan(null);
    setEditMode(false);
    setSearchMode(false);
    // Explicitly unhide the bottom sheet (belt-and-suspenders: useEffect may not re-fire
    // if searchMode/editMode were already false when executePlan is called from the CTA)
    useStore.getState().setTripPlannerOpen(false);
    useStore.getState().setBottomSheetHeight('peek');

    // Build waypoints list from confirmed rows only
    const wps = waypointRows
      .filter(w => w.dest !== null)
      .map(w => ({ lat: w.dest!.lat, lng: w.dest!.lng, name: w.dest!.place_name }));

    // Check cache first
    const cacheKey = planCacheKey(mode, originLat, originLng, travelDestination.lat, travelDestination.lng, wps, depIso);
    const cached = planCacheRef.current.get(cacheKey);
    if (cached) {
      setTravelPlan(cached);
      useStore.getState().setSelectedItineraryIndex(0);
      setTravelPlanLoading(false);
      return;
    }

    try {
      const plan = await apiService.getTravelPlan(
        originLat, originLng,
        travelDestination.lat, travelDestination.lng,
        travelDestination.place_name,
        travelOrigin?.place_name,
        depIso,
        mode,
        wps,
        Array.from(prayedToday),
      );
      setTravelPlan(plan);
      useStore.getState().setSelectedItineraryIndex(0);
      planCacheRef.current.set(cacheKey, plan);
    } catch {
      setTravelPlan(null);
      useStore.getState().setSelectedItineraryIndex(null);
      setTravelPlanError('Failed to plan route. Please check your connection and try again.');
    } finally {
      setTravelPlanLoading(false);
    }
  }

  function handlePlan() {
    if (!travelDestination) return;
    const originLat = travelOrigin?.lat ?? userLocation?.latitude;
    const originLng = travelOrigin?.lng ?? userLocation?.longitude;
    if (originLat && originLng && !travelModeStore) {
      const distKm = haversineKm(originLat, originLng, travelDestination.lat, travelDestination.lng);
      if (distKm > 80) {
        setLongTripModal(Math.round(distKm));
        return;
      }
    }
    executePlan(tripMode);
  }

  // Long-trip Musafir suggestion modal
  if (longTripModal !== null) {
    return (
      <div className="fixed inset-0 z-[600] flex items-center justify-center p-6 pointer-events-auto" style={{ background: 'rgba(0,0,0,0.4)' }}>
        <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm space-y-4">
          <h3 className="text-base font-bold text-gray-900">Long trip — ~{USE_METRIC ? `${longTripModal} km` : `${Math.round(longTripModal / 1.609)} mi`}</h3>
          <p className="text-sm text-gray-600">This trip is over {USE_METRIC ? '80 km' : '48 miles'}. As a Musafir (traveler) you can combine prayers (Dhuhr+Asr, Maghrib+Isha). Would you like to switch to Musafir mode?</p>
          <div className="flex gap-3">
            <button
              onClick={() => { setLongTripModal(null); setTravelMode(true); executePlan('travel'); }}
              className={`flex-1 text-white text-sm font-semibold py-2.5 rounded-xl ${th.bg} ${th.bgHover}`}
            >Switch to Musafir</button>
            <button
              onClick={() => { setLongTripModal(null); executePlan('driving'); }}
              className="flex-1 bg-white border border-gray-200 text-gray-700 text-sm font-semibold py-2.5 rounded-xl hover:bg-gray-50"
            >Continue as Muqeem</button>
          </div>
        </div>
      </div>
    );
  }

  // STATE 1: Idle — no destination selected
  if (!searchMode && !editMode && !travelDestination) {
    return (
      <div className="pt-3 pl-3 pb-3 pointer-events-auto" style={{ paddingRight: '220px' }}>
        <button
          onClick={() => { setSearchMode(true); useStore.getState().setTripPlannerOpen(true); }}
          className="w-full flex items-center h-12 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg px-4 text-left"
        >
          <span className="text-gray-400 text-sm font-medium">Where to?</span>
        </button>
      </div>
    );
  }

  // STATE 2: Destination search input
  if (searchMode) {
    return (
      <div className="pt-3 pl-3 pb-3 pointer-events-auto" style={{ paddingRight: '220px' }}>
        <div className="relative">
          <div className="flex items-center h-12 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg px-3 gap-2">
            <button
              onClick={() => setSearchMode(false)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 flex-shrink-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <input
              autoFocus
              type="text"
              placeholder="Where to?"
              value={destQuery}
              onChange={(e) => { setDestQuery(e.target.value); debounceGeocode(e.target.value, destDebounce, setDestLoading, setDestSugg); }}
              className="flex-1 outline-none bg-transparent text-gray-800 placeholder-gray-400 min-w-0"
              style={{ fontSize: 16 }}
            />
            {destLoading && <span className="text-xs text-gray-300 flex-shrink-0">…</span>}
            {destQuery && !destLoading && (
              <button
                onClick={() => { setDestQuery(''); setDestSugg([]); }}
                className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 text-sm leading-none"
              >×</button>
            )}
          </div>
          {destSugg.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden z-50">
              {destSugg.map((s, i) => (
                <button key={i}
                  onClick={() => {
                    setTravelDestination(s);
                    setDestQuery(s.place_name);
                    setDestSugg([]);
                    setSearchMode(false);
                    useStore.getState().setTripPlannerOpen(false);
                    useStore.getState().setBottomSheetHeight('peek');
                  }}
                  className="w-full text-left px-4 py-3.5 text-sm text-gray-800 border-b border-gray-50 last:border-0 hover:bg-gray-50"
                >
                  <p className="font-medium truncate">{s.place_name}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // STATE 3: Destination preview — show dest + "Plan a Trip" CTA
  if (travelDestination && !travelPlan && !editMode) {
    return (
      <>
        <div className="pt-3 pl-3 pb-3 pointer-events-auto" style={{ paddingRight: '220px' }}>
          <div className="flex items-center h-12 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg px-4 gap-3">
            <button
              onClick={() => setSearchMode(true)}
              className="flex-1 min-w-0 text-left h-full flex items-center"
            >
              <span className="text-sm font-medium text-gray-800 truncate block">{travelDestination.place_name}</span>
            </button>
            <button
              onClick={clearAll}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 text-xl leading-none"
            >✕</button>
          </div>
        </div>
        {/* Plan a Trip CTA — hidden while loading (bottom sheet shows the spinner) */}
        {!travelPlanLoading && (
          <div className="flex justify-center pb-3 pointer-events-auto">
            <button
              onClick={() => handlePlan()}
              className={`px-8 py-3 rounded-2xl text-sm font-semibold text-white shadow-lg active:scale-95 transition-transform ${th.bg} ${th.bgHover}`}
            >
              Pray on Route
            </button>
          </div>
        )}
      </>
    );
  }

  // STATE 4: Plan active chip — tap to edit, X to clear
  if (travelPlan && !editMode) {
    const originLabel = travelOrigin?.place_name ?? 'Current location';
    return (
      <div className="pt-3 pl-3 pb-3 pointer-events-auto" style={{ paddingRight: '220px' }}>
        <div
          onClick={() => { setEditMode(true); useStore.getState().setTripPlannerOpen(true); }}
          className="flex items-center gap-3 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg px-4 h-12 cursor-pointer"
        >
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${th.bg}`} />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 truncate">{originLabel}</p>
            <p className="text-sm font-semibold text-gray-800 truncate">{travelDestination!.place_name}</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none flex-shrink-0"
          >✕</button>
        </div>
      </div>
    );
  }

  // STATE 5: Edit mode — from/to/stops/done
  // (also handles loading state while re-planning from edit)
  const originLabel = travelOrigin?.place_name ?? 'Current location';
  return (
    <div className="p-3 pointer-events-auto">
      <div className="bg-white/97 backdrop-blur rounded-2xl shadow-xl overflow-hidden">
        <div className="px-3 pb-3 pt-3 space-y-2">
          {/* Back button — top-left only */}
          <div style={{ paddingRight: '200px' }}>
            <button
              onClick={() => {
                setEditMode(false);
                useStore.getState().setTripPlannerOpen(false);
              }}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
          </div>

          {/* From — with reset-to-current-location button */}
          <div className="flex items-center gap-1">
            <div className="flex-1 min-w-0">
              <GeoInput
                placeholder="Current location"
                icon="From:"
                value={originQuery}
                onChange={(v) => { setOriginQuery(v); debounceGeocode(v, originDebounce, setOriginLoading, setOriginSugg); }}
                suggestions={originSugg}
                onSelect={(s) => { setTravelOrigin(s); setOriginQuery(s.place_name); setOriginSugg([]); }}
                loading={originLoading}
                onClear={originQuery ? () => { setTravelOrigin(null); setOriginQuery(''); setOriginSugg([]); } : undefined}
              />
            </div>
            {userLocation && (
              <button
                onClick={() => {
                  setTravelOrigin(null);
                  setOriginSugg([]);
                  setOriginQuery('');
                  apiService.reverseGeocode(userLocation.latitude, userLocation.longitude)
                    .then((label) => { if (label) setOriginQuery(label); })
                    .catch(() => {});
                }}
                className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                title="Reset to current location"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/></svg>
              </button>
            )}
          </div>

          {/* Add stop — between From and To */}
          {travelDestination && waypointRows.length < 4 && (
            <button onClick={addWaypoint} className={`text-xs flex items-center gap-1 pl-1 ${th.textMid} ${th.textHoverDark}`}>
              <span className="text-sm font-bold">+</span> Add stop
            </button>
          )}

          {/* Intermediate waypoints (A, B, C...) */}
          {waypointRows.map((wp, i) => {
            const letter = String.fromCharCode(65 + i); // A, B, C, D
            return (
              <div key={wp.id} className="flex items-start gap-1">
                <div className="flex flex-col gap-0.5 pt-1.5">
                  <button onClick={() => moveWaypoint(i, -1)} disabled={i === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-20 text-xs leading-none px-0.5">▲</button>
                  <button onClick={() => moveWaypoint(i, 1)} disabled={i === waypointRows.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-20 text-xs leading-none px-0.5">▼</button>
                </div>
                <div className="flex-1 min-w-0">
                  <GeoInput
                    placeholder={`Stop ${letter}`}
                    icon={`${letter}:`}
                    value={wp.query}
                    onChange={(v) => debounceGeocodeWp(wp.id, i, v)}
                    suggestions={wp.sugg}
                    onSelect={(s) => setWaypointRows(rows => rows.map(r => r.id === wp.id ? { ...r, dest: s, query: s.place_name, sugg: [] } : r))}
                    loading={wp.loading}
                    onClear={() => setWaypointRows(rows => rows.map(r => r.id === wp.id ? { ...r, dest: null, query: '', sugg: [] } : r))}
                  />
                </div>
                <button onClick={() => removeWaypoint(i)} className="text-gray-400 hover:text-red-500 text-base leading-none pt-2 px-1 flex-shrink-0">✕</button>
              </div>
            );
          })}

          {/* To / Destination */}
          <GeoInput
            placeholder="Where to?"
            icon="To:"
            value={destQuery}
            onChange={(v) => { setDestQuery(v); debounceGeocode(v, destDebounce, setDestLoading, setDestSugg); }}
            suggestions={destSugg}
            onSelect={(s) => { setTravelDestination(s); setDestQuery(s.place_name); setDestSugg([]); }}
            loading={destLoading}
          />

          {/* Departure time */}
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
            <input
              type="datetime-local"
              value={departureInput}
              onChange={(e) => setDepartureInput(e.target.value)}
              className="flex-1 text-sm outline-none bg-transparent text-gray-700 min-w-0"
            />
          </div>

          {/* Long-trip suggestion */}
          {longTripKm !== null && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-amber-800">Long trip — ~{USE_METRIC ? `${longTripKm} km` : `${Math.round(longTripKm / 1.609)} mi`}</p>
              <p className="text-xs text-amber-700">As Musafir you can combine prayers (Dhuhr+Asr, Maghrib+Isha).</p>
              <div className="flex gap-2">
                <button onClick={() => { setTravelMode(true); executePlan('travel'); }} className={`flex-1 text-white text-xs font-semibold py-1.5 rounded-lg ${th.bg} ${th.bgHover}`}>Switch to Musafir</button>
                <button onClick={() => executePlan('driving')} className="flex-1 bg-white border border-gray-300 text-gray-600 text-xs font-semibold py-1.5 rounded-lg hover:bg-gray-50">Plan as Muqeem</button>
              </div>
            </div>
          )}

          {/* Done button */}
          {longTripKm === null && (
            <button
              onClick={handlePlan}
              disabled={!travelDestination || travelPlanLoading}
              className={`w-full text-white text-sm font-semibold py-2.5 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${th.bg} ${th.bgHover}`}
            >
              {travelPlanLoading ? 'Planning…' : 'Done'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Travel Plan View ────────────────────────────────────────────────────────

function TravelItineraryCard({ itinerary, index }: { itinerary: TripItinerary; index: number }) {
  const setSelectedMosqueId       = useStore((s) => s.setSelectedMosqueId);
  const setBottomSheetHeight      = useStore((s) => s.setBottomSheetHeight);
  const setMapFocusCoords         = useStore((s) => s.setMapFocusCoords);
  const selectedItineraryIndex    = useStore((s) => s.selectedItineraryIndex);
  const setSelectedItineraryIndex = useStore((s) => s.setSelectedItineraryIndex);
  const th                        = useTheme();
  const [expanded, setExpanded]   = useState(index === 0);


  const isSelected = selectedItineraryIndex === index;

  return (
    <div className={`mx-3 bg-white border rounded-xl shadow-sm transition-all ${isSelected ? `${th.borderStrong} ${th.shadow}` : itinerary.feasible ? 'border-gray-200' : 'border-gray-200 opacity-60'}`}>
      {/* Header: left area selects itinerary; chevron button only expands/collapses */}
      <div className="flex items-start px-3 pt-3 pb-2 gap-2">
        <button
          className="flex-1 text-left min-w-0"
          onClick={() => setSelectedItineraryIndex(index)}
        >
          <p className={`text-xs font-bold uppercase tracking-wide ${isSelected ? th.textMid : th.text}`}>
            {isSelected ? '▶ ' : ''}Option {index + 1}
          </p>
          <p className="text-sm font-semibold text-gray-800 mt-0.5 leading-snug">{stripEmoji(itinerary.label)}</p>
          {(() => {
            // Compute arrival time for this itinerary (base + detour)
            try {
              const plan = useStore.getState().travelPlan;
              if (!plan) return null;
              const baseArr = new Date(plan.estimated_arrival_time);
              const withDetour = new Date(baseArr.getTime() + itinerary.total_detour_minutes * 60000);
              const arrTime = withDetour.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
              const dep = new Date(plan.departure_time);
              const depDay = dep.toLocaleDateString([], { month: 'short', day: 'numeric' });
              const arrDay = withDetour.toLocaleDateString([], { month: 'short', day: 'numeric' });
              const dayLabel = depDay !== arrDay
                ? ` ${withDetour.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}`
                : '';
              const detourLabel = itinerary.total_detour_minutes > 0
                ? ` · +${fmtDuration(itinerary.total_detour_minutes)} detour`
                : '';
              return (
                <p className="text-xs text-gray-500 mt-0.5">
                  Arrive {arrTime}{dayLabel}{detourLabel}
                </p>
              );
            } catch { return null; }
          })()}
        </button>
        <button
          className="shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? '▲' : '▼'}
        </button>
      </div>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-gray-100 pt-2">
          {itinerary.pair_choices.map((pc: PairChoice, i: number) => {
            return (
              <div key={i}>
                {/* Prayer pair header */}
                <div className="flex items-center gap-1.5 mb-1 min-w-0 overflow-hidden">
                  <span className="text-xs font-semibold text-gray-700 truncate">{stripEmoji(pc.label)}</span>
                  {pc.option.combination_label && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 border ${th.bgLight} ${th.text} ${th.border}`}>
                      {stripEmoji(pc.option.combination_label)}
                    </span>
                  )}
                </div>
                {/* Mosque stops — tappable */}
                {pc.option.stops.map((stop: TravelStop, j: number) => (
                  <button
                    key={j}
                    className={`w-full text-left text-xs bg-gray-50 rounded-lg px-2.5 py-1.5 mt-1 border border-gray-100 transition-colors overflow-hidden ${th.borderHover} ${th.bgHoverLight}`}
                    onClick={() => {
                      setSelectedMosqueId(stop.mosque_id);
                      setMapFocusCoords({ lat: stop.mosque_lat, lng: stop.mosque_lng });
                      setBottomSheetHeight('peek');
                    }}
                  >
                    <span className="font-medium text-gray-800">{stop.mosque_name}</span>
                    {stop.mosque_address ? <span className="text-gray-500"> · {stop.mosque_address}</span> : null}
                    {stop.iqama_time ? <span className="text-gray-600"> · Iqama {fmtTime(stop.iqama_time)}</span> : null}
                    {stop.estimated_arrival_time && (
                      <span className="text-gray-500"> · Arrive ~{fmtTime(stop.estimated_arrival_time)}</span>
                    )}
                    {stop.detour_minutes > 0
                      ? <span className={`ml-1 ${th.textMid}`}> +{fmtDuration(stop.detour_minutes)} detour</span>
                      : <span className="ml-1 text-gray-400"> Near departure</span>
                    }
                  </button>
                ))}
                {pc.option.note && (
                  <p className="text-xs text-gray-400 mt-1 italic">{pc.option.note}</p>
                )}
              </div>
            );
          })}

        </div>
      )}
    </div>
  );
}

// ─── Navigate Bar (floats over the map when an itinerary is selected) ────────

function NavigateBar() {
  const th                     = useTheme();
  const selectedItineraryIndex = useStore((s) => s.selectedItineraryIndex);
  const travelPlan        = useStore((s) => s.travelPlan);
  const travelOrigin      = useStore((s) => s.travelOrigin);
  const travelDestination = useStore((s) => s.travelDestination);
  const userLocation      = useStore((s) => s.userLocation);
  const setNavShareOpen   = useStore((s) => s.setNavShareOpen);
  const tripPlannerOpen   = useStore((s) => s.tripPlannerOpen);
  const sortBy            = useStore((s) => s.travelSortBy);
  const [sheetOpen, setSheetOpen] = useState(false);

  function openSheet() { setSheetOpen(true);  setNavShareOpen(true); }
  function closeSheet() { setSheetOpen(false); setNavShareOpen(false); }

  if (tripPlannerOpen) return null;

  if (selectedItineraryIndex == null || !travelPlan || !travelDestination) return null;

  const sorted = sortItineraries(travelPlan.itineraries ?? [], sortBy);
  const itinerary = sorted[selectedItineraryIndex];
  if (!itinerary) return null;

  const originLat = travelOrigin?.lat ?? userLocation?.latitude;
  const originLng = travelOrigin?.lng ?? userLocation?.longitude;
  if (!originLat || !originLng) return null;

  const seenIds = new Set<string>();
  const waystops = itinerary.pair_choices
    .flatMap((pc: PairChoice) => pc.option.stops)
    .sort((a: TravelStop, b: TravelStop) => a.minutes_into_trip - b.minutes_into_trip)
    .filter((s: TravelStop) => { if (seenIds.has(s.mosque_id)) return false; seenIds.add(s.mosque_id); return true; });

  const points: MapPoint[] = [
    { lat: originLat, lng: originLng, name: travelOrigin?.place_name, is_gps: !travelOrigin },
    ...waystops.map((s: TravelStop) => ({
      lat: s.mosque_lat, lng: s.mosque_lng,
      name: s.mosque_address ? `${s.mosque_name}, ${s.mosque_address}` : s.mosque_name,
      place_id: s.google_place_id,
    })),
    { lat: travelDestination.lat, lng: travelDestination.lng, name: travelDestination.place_name },
  ];

  const googleUrl = buildGoogleMapsUrl(points);
  const appleUrl  = buildAppleMapsUrl(points);

  async function handleShare() {
    if (navigator.share) {
      try { await navigator.share({ title: `Prayer route — Option ${selectedItineraryIndex! + 1}`, text: itinerary.summary, url: googleUrl }); return; }
      catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(googleUrl); } catch { window.open(googleUrl, '_blank'); }
  }

  return (
    <>
      {/* Sits below the search bar in the top overlay — rendered as pointer-events-auto strip */}
      <div className="px-3 pb-2 pointer-events-auto">
        <button
          onClick={openSheet}
          className={`w-full flex items-center justify-center gap-2 active:scale-95 text-white text-sm font-semibold rounded-xl py-3 shadow-lg transition-all ${th.bgAccent} ${th.bgAccentHover}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
          </svg>
          بسم الله — Navigate
        </button>
      </div>

      {/* Full-screen action sheet — portaled to document.body so it sits above all stacking contexts */}
      {sheetOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[1000] flex items-end bg-black/50" onClick={closeSheet}>
          <div className="w-full bg-white rounded-t-2xl shadow-2xl pb-8 px-4 pt-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center mb-4">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <p className="text-sm font-semibold text-gray-800 mb-0.5">Option {selectedItineraryIndex! + 1}: {stripEmoji(itinerary.label)}</p>
            <p className="text-xs text-gray-400 mb-4">{stripEmoji(itinerary.summary)}</p>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Open route in…</p>
            <div className="space-y-2">
              <button
                onClick={() => { window.open(googleUrl, '_blank'); closeSheet(); }}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-gray-50 active:bg-gray-100 text-sm font-medium text-gray-800 transition-colors"
              >
                <img src="https://www.google.com/favicon.ico" alt="" className="w-5 h-5 rounded" />
                Google Maps
              </button>
              {IS_IOS && (
                <button
                  onClick={() => { window.open(appleUrl, '_blank'); closeSheet(); }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-gray-50 active:bg-gray-100 text-sm font-medium text-gray-800 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 814 1000" fill="currentColor">
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.8-155.5-127.5c-43.5-74.2-77.5-188.6-77.5-297.5 0-179 116.7-273.8 231.5-273.8 61.5 0 112.8 40.8 150.7 40.8 36.2 0 93.8-43.4 162.8-43.4 26.2 0 108.2 2.6 168.3 92.8zm-107-99.4C720.8 168 743.1 111.8 743.1 55c0-5.8-.6-11.6-1.9-16.5-57.3 2.6-124.9 38.9-166.2 89.7-36.2 43.4-70.1 113.1-70.1 182.7 0 6.4 1.3 12.8 1.9 15.5 3.9.6 10.2 1.3 16.5 1.3 50.6 0 113.5-33.9 157.8-86.1z"/>
                  </svg>
                  Apple Maps
                </button>
              )}
              <button
                onClick={async () => { await handleShare(); closeSheet(); }}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-gray-50 active:bg-gray-100 text-sm font-medium text-gray-800 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                Share Route
              </button>
            </div>
            <button
              onClick={closeSheet}
              onTouchEnd={(e) => { e.stopPropagation(); closeSheet(); }}
              className="w-full mt-3 py-3 text-sm font-semibold text-gray-400 active:text-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

type SortOption = 'recommended' | 'least_detour' | 'fewest_stops' | 'most_imam';

function sortItineraries(its: TripItinerary[], sort: SortOption): TripItinerary[] {
  const copy = [...its];
  switch (sort) {
    case 'least_detour':
      return copy.sort((a, b) => a.total_detour_minutes - b.total_detour_minutes);
    case 'fewest_stops':
      return copy.sort((a, b) => a.stop_count - b.stop_count || a.total_detour_minutes - b.total_detour_minutes);
    case 'most_imam':
      return copy.sort((a, b) => {
        const countImam = (it: TripItinerary) => it.pair_choices.reduce((n, pc) =>
          n + pc.option.stops.filter((s: TravelStop) => s.status === 'can_catch_with_imam').length, 0);
        return countImam(b) - countImam(a);
      });
    default:
      return copy; // 'recommended' — already ranked by server
  }
}

function TravelPlanView() {
  const th                = useTheme();
  const travelPlan        = useStore((s) => s.travelPlan);
  const travelPlanLoading = useStore((s) => s.travelPlanLoading);
  const travelPlanError   = useStore((s) => s.travelPlanError);
  const travelDestination = useStore((s) => s.travelDestination);
  const sortBy            = useStore((s) => s.travelSortBy);
  const setSortBy         = useStore((s) => s.setTravelSortBy);

  const sortedItineraries = useMemo(
    () => sortItineraries(travelPlan?.itineraries ?? [], sortBy),
    [travelPlan?.itineraries, sortBy]
  );

  if (!travelDestination) return null;

  if (travelPlanLoading) {
    return (
      <div className="mx-3 py-10 flex flex-col items-center gap-3">
        <svg className={`animate-spin h-7 w-7 ${th.textMid}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
        <p className="text-sm text-gray-400">Planning your prayer route…</p>
      </div>
    );
  }

  if (!travelPlan) {
    if (travelPlanError) {
      return (
        <div className="mx-3 py-6">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
            <p className="text-sm text-red-700">{travelPlanError}</p>
          </div>
        </div>
      );
    }
    return null;
  }

  const { route, itineraries } = travelPlan;
  const durationHrs  = Math.floor(route.duration_minutes / 60);
  const durationMins = route.duration_minutes % 60;
  const distDisplay  = USE_METRIC
    ? `${(route.distance_meters / 1000).toFixed(0)} km`
    : `${Math.round(route.distance_meters / 1609.344)} mi`;

  return (
    <div className="space-y-3 pb-4">
      {/* Route summary */}
      <div className="mx-3 bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-600">
        <span className="font-semibold text-gray-800">Route: </span>
        {durationHrs > 0 ? `${durationHrs}h ` : ''}{durationMins}min · {distDisplay}
        {(() => {
          // Show arrival day if trip crosses midnight
          try {
            const dep = new Date(travelPlan.departure_time);
            const arr = new Date(travelPlan.estimated_arrival_time);
            const depDay = dep.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
            const arrDay = arr.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
            const arrTime = arr.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            if (depDay !== arrDay) {
              const dayDiff = Math.round((arr.getTime() - dep.getTime()) / 86400000);
              return <span className="text-gray-500"> · Arrive {arrTime} {dayDiff === 1 ? '(+1 day)' : `(+${dayDiff} days)`}</span>;
            }
          } catch {}
          return null;
        })()}
      </div>

      {/* Itinerary count + sort selector */}
      {itineraries && itineraries.length > 0 && (
        <div className="mx-3 flex items-center justify-between">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
            {itineraries.length} prayer plan{itineraries.length !== 1 ? 's' : ''}
          </p>
          {itineraries.length > 1 && (
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="text-xs text-gray-500 bg-transparent border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none"
            >
              <option value="recommended">Recommended</option>
              <option value="least_detour">Least detour</option>
              <option value="fewest_stops">Fewest stops</option>
              <option value="most_imam">Most prayers with Imam</option>
            </select>
          )}
        </div>
      )}

      {/* Complete trip itineraries (sorted) */}
      {sortedItineraries.map((it: TripItinerary, i: number) => (
        <TravelItineraryCard key={`${sortBy}-${i}`} itinerary={it} index={i} />
      ))}

      {(!itineraries || itineraries.length === 0) && (
        <div className="mx-3 text-center py-6 text-gray-400 text-sm">
          No prayer stops found along this route.
        </div>
      )}
    </div>
  );
}

// ─── Mode Toggle ─────────────────────────────────────────────────────────────

function ModeToggle() {
  const travelMode    = useStore((s) => s.travelMode);
  const setTravelMode = useStore((s) => s.setTravelMode);
  const th            = useTheme();
  return (
    <div className="flex h-12 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden border border-gray-200 flex-shrink-0">
      <button
        onClick={() => setTravelMode(false)}
        className={`px-3 h-full text-xs font-semibold transition-colors ${
          !travelMode ? `${th.bg} text-white` : 'text-gray-500'
        }`}
      >
        Muqeem
      </button>
      <div className="w-px bg-gray-200 self-stretch" />
      <button
        onClick={() => setTravelMode(true)}
        className={`px-3 h-full text-xs font-semibold transition-colors ${
          travelMode ? `${th.bg} text-white` : 'text-gray-500'
        }`}
      >
        Musafir
      </button>
    </div>
  );
}

// ─── Add Spot FAB ─────────────────────────────────────────────────────────────

function AddSpotFAB() {
  const openSheet          = useStore((s) => s.openSheet);
  const showSpots          = useStore((s) => s.showSpots);
  const travelDestination  = useStore((s) => s.travelDestination);
  const bottomSheetHeight  = useStore((s) => s.bottomSheetHeight);
  const tripPlannerOpen    = useStore((s) => s.tripPlannerOpen);
  const th                 = useTheme();

  if (!showSpots || travelDestination || bottomSheetHeight === 'full' || tripPlannerOpen) return null;

  return (
    <button
      onClick={() => openSheet({ type: 'spot_submit' })}
      title="Add a prayer spot"
      style={{ bottom: 'calc(var(--sheet-visible, 125px) + 64px)', right: '16px' }}
      className={`fixed z-[495] w-10 h-10 rounded-full ${th.bg} ${th.bgHover} text-white shadow-xl flex items-center justify-center text-xl font-light active:scale-95`}
    >
      +
    </button>
  );
}

// ─── Map Bottom Sheet ─────────────────────────────────────────────────────────

function MapBottomSheet() {
  const th                  = useTheme();
  const bottomSheetHeight   = useStore((s) => s.bottomSheetHeight);
  const setBottomSheetHeight = useStore((s) => s.setBottomSheetHeight);
  const travelDestination   = useStore((s) => s.travelDestination);
  const travelPlan          = useStore((s) => s.travelPlan);
  const travelPlanLoading   = useStore((s) => s.travelPlanLoading);
  const selectedItineraryIndex = useStore((s) => s.selectedItineraryIndex);
  const mosquesLoading      = useStore((s) => s.mosquesLoading);
  const mosquesError        = useStore((s) => s.mosquesError);
  const mosques             = useStore((s) => s.mosques);
  const spots               = useStore((s) => s.spots);
  const spotsLoading        = useStore((s) => s.spotsLoading);
  const showSpots           = useStore((s) => s.showSpots);
  const userLocation        = useStore((s) => s.userLocation);
  const singleMosqueNav     = useStore((s) => s.singleMosqueNav);
  const setSingleMosqueNav  = useStore((s) => s.setSingleMosqueNav);
  const setSelectedMosqueId = useStore((s) => s.setSelectedMosqueId);

  const tripPlannerOpen = useStore((s) => s.tripPlannerOpen);
  const bottomSheet     = useStore((s) => s.bottomSheet);

  const sheetRef        = useRef<HTMLDivElement>(null);
  const dragStartY      = useRef<number | null>(null);
  const dragStartOffset = useRef(0);
  const draggedRef      = useRef(false);

  // Use the actual rendered height so JS perfectly tracks the CSS value (which uses env(safe-area-inset-top))
  function getSnappedY(state: 'peek' | 'half' | 'full'): number {
    const sheetH = sheetRef.current?.offsetHeight ?? (window.innerHeight - 120);
    if (state === 'full') return 0;
    if (state === 'half') return Math.max(0, sheetH - sheetH * 0.55);
    return Math.max(0, sheetH - 125); // peek: show 125px
  }

  // Animate sheet when state changes, or hide fully when trip form is open.
  //
  // IMPORTANT: --sheet-visible is computed purely from the snap state formula,
  // NOT from DOM measurements. DOM measurements (offsetHeight) are unreliable
  // because --top-bar-bottom changes asynchronously (ResizeObserver), causing
  // offsetHeight to shift between the sync read and a rAF callback. This was
  // the root cause of buttons flying to the top on first render.
  //
  // The visible height for each state:
  //   peek: always 125px (by definition — getSnappedY returns sheetH - 125)
  //   half: sheetH * 0.55 (getSnappedY returns sheetH - sheetH*0.55 = sheetH*0.45)
  //   full: sheetH (getSnappedY returns 0)
  //
  // For half, we need the actual sheetH. We compute it at snap time (not in rAF)
  // so the value is consistent with the translateY we just set.
  useLayoutEffect(() => {
    if (!sheetRef.current) return;
    sheetRef.current.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
    const sheetH = sheetRef.current.offsetHeight;
    const y = tripPlannerOpen
      ? window.innerHeight
      : getSnappedY(bottomSheetHeight);
    sheetRef.current.style.transform = `translateY(${y}px)`;
    // Compute visible from the SAME sheetH used for y — no timing mismatch possible
    const visible = tripPlannerOpen ? 0 : Math.max(0, sheetH - y);
    document.documentElement.style.setProperty('--sheet-visible', `${visible}px`);
  }, [bottomSheetHeight, tripPlannerOpen, travelDestination]);

  // Re-sync --sheet-visible when --top-bar-bottom changes (the sheet's offsetHeight shifts)
  useEffect(() => {
    function resync() {
      if (!sheetRef.current || tripPlannerOpen) return;
      const sheetH = sheetRef.current.offsetHeight;
      const y = getSnappedY(bottomSheetHeight);
      sheetRef.current.style.transform = `translateY(${y}px)`;
      const visible = Math.max(0, sheetH - y);
      document.documentElement.style.setProperty('--sheet-visible', `${visible}px`);
    }
    window.addEventListener('topbar-resized', resync);
    return () => window.removeEventListener('topbar-resized', resync);
  }); // no deps — always uses latest state via closure

  // Auto-transitions
  useEffect(() => {
    if (travelPlan) setBottomSheetHeight('half');
  }, [travelPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!travelDestination) setBottomSheetHeight('peek');
  }, [travelDestination]); // eslint-disable-line react-hooks/exhaustive-deps

  // When a detail modal closes, pull the sheet back to half if it was maximized
  // so that FAB and location button become visible again
  const prevBottomSheetRef = useRef(bottomSheet);
  useEffect(() => {
    if (prevBottomSheetRef.current !== null && bottomSheet === null && bottomSheetHeight === 'full') {
      setBottomSheetHeight('half');
    }
    prevBottomSheetRef.current = bottomSheet;
  }, [bottomSheet]); // eslint-disable-line react-hooks/exhaustive-deps

  // Snap to half when a mosque is selected (and not in trip mode)
  useEffect(() => {
    if (singleMosqueNav && !travelDestination) setBottomSheetHeight('half');
  }, [singleMosqueNav]); // eslint-disable-line react-hooks/exhaustive-deps

  function dismissMosque() {
    setSingleMosqueNav(null);
    setSelectedMosqueId(null);
    setBottomSheetHeight('peek');
  }

  function handleTouchStart(e: React.TouchEvent) {
    draggedRef.current = false;
    dragStartY.current = e.touches[0].clientY;
    if (sheetRef.current) {
      const m = new DOMMatrix(getComputedStyle(sheetRef.current).transform);
      dragStartOffset.current = m.m42;
      sheetRef.current.style.transition = 'none';
    }
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (dragStartY.current === null || !sheetRef.current) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    if (Math.abs(delta) > 5) draggedRef.current = true;
    const newY = Math.max(0, Math.min(dragStartOffset.current + delta, getSnappedY('peek')));
    sheetRef.current.style.transform = `translateY(${newY}px)`;
    // Keep buttons tracking the sheet in real-time during drag
    const sheetH = sheetRef.current.offsetHeight;
    const dragVisible = Math.min(Math.max(0, sheetH - newY), window.innerHeight * 0.8);
    document.documentElement.style.setProperty('--sheet-visible', `${dragVisible}px`);
  }

  function handleTouchEnd() {
    if (dragStartY.current === null || !sheetRef.current) return;
    dragStartY.current = null;
    const m = new DOMMatrix(getComputedStyle(sheetRef.current).transform);
    const currentY = m.m42;
    const states: Array<'peek' | 'half' | 'full'> = ['peek', 'half', 'full'];
    const nearest = states.reduce((a, b) =>
      Math.abs(getSnappedY(a) - currentY) < Math.abs(getSnappedY(b) - currentY) ? a : b
    );
    setBottomSheetHeight(nearest);
  }

  function handlePeekTap() {
    if (draggedRef.current) return;
    setBottomSheetHeight(bottomSheetHeight === 'full' ? 'peek' : 'full');
  }

  // Peek label
  let peekLabel = '';
  if (singleMosqueNav && !travelDestination) {
    peekLabel = '';
  } else if (selectedItineraryIndex !== null && travelPlan?.itineraries?.length) {
    const sorted = sortItineraries(travelPlan.itineraries ?? [], useStore.getState().travelSortBy);
    const it = sorted[selectedItineraryIndex];
    if (it) {
      const detour = it.total_detour_minutes > 0 ? ` · +${fmtDuration(it.total_detour_minutes)} detour` : '';
      peekLabel = bottomSheetHeight === 'full'
        ? `Option ${selectedItineraryIndex + 1}${detour} — tap to minimize`
        : `Option ${selectedItineraryIndex + 1}${detour} — tap to see all`;
    }
  } else if (travelPlanLoading) {
    peekLabel = 'Finding prayer routes…';
  } else if (travelPlan?.itineraries?.length) {
    peekLabel = `${travelPlan.itineraries.length} prayer route${travelPlan.itineraries.length > 1 ? 's' : ''}`;
  } else if (travelDestination) {
    peekLabel = `Route to ${travelDestination.place_name}`;
  } else if (mosquesLoading) {
    peekLabel = 'Finding mosques nearby…';
  } else {
    peekLabel = mosques.length > 0 ? `${mosques.length} mosques nearby` : 'Nearby prayer places';
  }

  return (
    <div
      ref={sheetRef}
      className="fixed left-0 right-0 bottom-0 bg-white rounded-t-3xl shadow-2xl z-[400] overflow-hidden pointer-events-none"
      style={{ top: 'var(--top-bar-bottom, 140px)', transform: `translateY(${getSnappedY(bottomSheetHeight)}px)` }}
    >
      {/* Combined drag handle + peek label — the whole strip is swipeable and tappable */}
      <div
        className="pointer-events-auto select-none cursor-grab active:cursor-grabbing touch-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handlePeekTap}
      >
        <div className="pt-3 pb-1 flex justify-center">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>
        {peekLabel ? (
          <div className="px-4 py-2 active:bg-gray-50">
            <p className="text-sm font-semibold text-gray-800 leading-tight truncate">{peekLabel}</p>
          </div>
        ) : (
          <div className="pb-1" />
        )}
      </div>

      {/* Scrollable content */}
      <div className="overflow-y-auto pointer-events-auto" style={{ height: 'calc(100% - 64px)' }}>

        {/* Mosque detail view — shown when mosque is selected and no trip active */}
        {singleMosqueNav && !travelDestination ? (
          <div className="px-4 pt-2 pb-6">
            <MosqueDetailSheet mosque={singleMosqueNav} onDismiss={dismissMosque} />
          </div>
        ) : travelDestination ? (
          <TravelPlanView />
        ) : (
          <>
            {/* Loading mosques */}
            {mosquesLoading && (
              <div className="mx-3 py-10 flex flex-col items-center gap-3">
                <svg className={`animate-spin h-7 w-7 ${th.textMid}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                <p className="text-sm text-gray-400">Finding mosques nearby…</p>
              </div>
            )}

            {/* Error */}
            {mosquesError && !mosquesLoading && (
              <div className="mx-4 mt-2 bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700">
                {mosquesError}
              </div>
            )}

            {/* No location */}
            {!userLocation && !mosquesLoading && !mosquesError && (
              <div className="flex flex-col items-center py-16 gap-2 text-slate-400">
                <p className="text-3xl">📍</p>
                <p className="text-sm font-medium">Waiting for your location…</p>
              </div>
            )}

            {/* Prayed banner */}
            {userLocation && !mosquesLoading && <PrayedBanner mosques={mosques} />}

            {/* Mosque list */}
            {!mosquesLoading && mosques.length > 0 && (
              <div className="px-3 pt-1 pb-3 space-y-2.5">
                {mosques.map((m) => <MosqueCard key={m.id} mosque={m} />)}
              </div>
            )}

            {/* Prayer spots */}
            {showSpots && !travelDestination && (
              <div className="px-3 pb-6">
                {spotsLoading && <p className="text-xs text-slate-400 px-1 py-2">Loading spots…</p>}
                {!spotsLoading && spots.length > 0 && (
                  <>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 mt-1 px-1">Prayer Spots</p>
                    <div className="space-y-2">
                      {spots.map((s) => <SpotCard key={s.id} spot={s} />)}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Last resort */}
            {userLocation && !mosquesLoading && !spotsLoading && (() => {
              const CATCHABLE = new Set(['can_catch_with_imam','can_catch_with_imam_in_progress','can_pray_solo_at_mosque','upcoming']);
              if (mosques.some(m => m.next_catchable && CATCHABLE.has(m.next_catchable.status))) return null;
              if (spots.some(s => s.status === 'active')) return null;
              return <div className="px-3 pb-4"><LastResortCard /></div>;
            })()}
          </>
        )}
      </div>
    </div>
  );
}


// ─── Main App ────────────────────────────────────────────────────────────────

function App() {
  const userLocation  = useStore((s) => s.userLocation);
  const setUserLocation = useStore((s) => s.setUserLocation);
  const setMosques    = useStore((s) => s.setMosques);
  const setMosquesLoading = useStore((s) => s.setMosquesLoading);
  const setMosquesError = useStore((s) => s.setMosquesError);
  const setSpots      = useStore((s) => s.setSpots);
  const setSpotsLoading = useStore((s) => s.setSpotsLoading);
  const radiusKm      = useStore((s) => s.radiusKm);
  const denominationFilter = useStore((s) => s.denominationFilter);
  const showSpots     = useStore((s) => s.showSpots);
  const travelModeStore = useStore((s) => s.travelMode);
  const travelDestination = useStore((s) => s.travelDestination);
  const setTravelPlan = useStore((s) => s.setTravelPlan);

  // Initialize --sheet-visible immediately so LocationButton/FAB are positioned correctly on first render
  // useLayoutEffect fires synchronously before paint, preventing the flash of wrong position
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--sheet-visible', '125px');
  }, []);

  // Measure the top overlay height and expose as CSS var so the bottom sheet
  // always starts exactly below the search bar (accounting for safe area + Dynamic Island)
  const topOverlayRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = topOverlayRef.current;
    if (!el) return;
    function update() {
      const bottom = el!.getBoundingClientRect().bottom;
      // Guard against pre-layout 0 values; +8px adds visual gap below search bar
      if (bottom > 20) {
        const val = `${bottom + 8}px`;
        const prev = document.documentElement.style.getPropertyValue('--top-bar-bottom');
        document.documentElement.style.setProperty('--top-bar-bottom', val);
        // When --top-bar-bottom changes, the sheet's offsetHeight changes too.
        // Re-sync --sheet-visible so buttons don't fly to the wrong position.
        if (prev !== val) {
          // Dispatch a custom event that MapBottomSheet listens to
          window.dispatchEvent(new Event('topbar-resized'));
        }
      }
    }
    // Multiple attempts: rAF catches the first paint, timeout catches iOS deferred safe-area layout
    const raf = requestAnimationFrame(() => {
      update();
      setTimeout(update, 150);
      setTimeout(update, 500);
    });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  // Geolocation on mount
  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setMosquesError('Geolocation is not supported by this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      },
      () => setMosquesError('Please enable location access to find nearby mosques.'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link / Web Share Target handler — runs once on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let handled = false;

    // Direct params: ?dest_lat=X&dest_lng=Y&dest_name=Z (programmatic deep link)
    const destLat  = params.get('dest_lat');
    const destLng  = params.get('dest_lng');
    const destName = params.get('dest_name');
    if (destLat && destLng) {
      // Set destination only — do NOT change travel mode (PRAYER_LOGIC_RULES §5)
      useStore.getState().setTravelDestination({
        lat: parseFloat(destLat),
        lng: parseFloat(destLng),
        place_name: destName ?? 'Shared destination',
      });
      handled = true;
    } else if (params.get('share') === 'maps') {
      // Web Share Target: URL shared from Google Maps / Apple Maps
      const sharedUrl   = params.get('url')   ?? '';
      const sharedTitle = params.get('title') ?? params.get('text') ?? '';
      const parsed = parseMapShareUrl(sharedUrl);
      if (parsed) {
        useStore.getState().setTravelDestination(parsed);
      } else if (sharedTitle) {
        // Shortened URL (goo.gl/maps.app.goo.gl) — pre-fill search with title
        sessionStorage.setItem('cap_pending_dest', sharedTitle);
      }
      handled = true;
    }

    if (handled) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch when location, radius, or travel mode changes, and auto-refresh with backoff
  const refreshFailCount = useRef(0);
  useEffect(() => {
    if (!userLocation) return;
    fetchData(userLocation.latitude, userLocation.longitude)
      .then(() => { refreshFailCount.current = 0; })
      .catch(() => { refreshFailCount.current = Math.min(refreshFailCount.current + 1, 3); });

    const BASE_INTERVAL = 5 * 60 * 1000; // 5 minutes
    let timer: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      const backoff = BASE_INTERVAL * Math.pow(2, refreshFailCount.current); // 5→10→20→40 min
      timer = setTimeout(async () => {
        try {
          await fetchData(userLocation.latitude, userLocation.longitude);
          refreshFailCount.current = 0;
        } catch {
          refreshFailCount.current = Math.min(refreshFailCount.current + 1, 3);
        }
        scheduleNext();
      }, backoff);
    };
    scheduleNext();
    return () => clearTimeout(timer);
  }, [userLocation, radiusKm, travelModeStore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear travel plan when destination is removed
  useEffect(() => {
    if (!travelDestination) { setTravelPlan(null); useStore.getState().setSelectedItineraryIndex(null); }
  }, [travelDestination]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchData(lat: number, lng: number) {
    setMosquesLoading(true);
    setMosquesError(null);
    console.log('[fetchData] lat:', lat, 'lng:', lng, 'radius:', radiusKm, 'travel:', travelModeStore);
    try {
      const res = await apiService.findNearbyMosques(lat, lng, radiusKm, travelModeStore);
      if (!res || !Array.isArray(res.mosques)) {
        throw new Error('Invalid response from server — is REACT_APP_API_URL set?');
      }
      let filtered = res.mosques;
      if (denominationFilter !== 'all') {
        filtered = filtered.filter(
          (m) => m.denomination?.toLowerCase().includes(denominationFilter)
        );
      }
      setMosques(filtered);
    } catch (e: any) {
      console.error('[fetchData] error:', e?.message, e?.code, e?.response?.status, e?.response?.data, e);
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'no_mosques_found') {
        setMosquesError(`No mosques found within ${USE_METRIC ? `${radiusKm} km` : `${Math.round(radiusKm / 1.60934)} mi`}. Try increasing the search radius in Settings.`);
      } else {
        setMosquesError(typeof detail === 'string' ? detail : 'Failed to load mosques.');
      }
    } finally {
      setMosquesLoading(false);
    }

    if (showSpots) {
      setSpotsLoading(true);
      try {
        const res2 = await apiService.findNearbySpots(lat, lng, radiusKm, SESSION_ID);
        setSpots(res2.spots);
      } catch {
        setSpots([]);
      } finally {
        setSpotsLoading(false);
      }
    }
  }

  return (
    <div className="fixed inset-0 overflow-hidden bg-slate-200">
      {/* Layer 0: Full-screen map */}
      <div className="absolute inset-0">
        <Suspense fallback={<div className="h-full bg-slate-200" />}>
          <MapView />
        </Suspense>
      </div>

      {/* Layer 1: Top overlay — trip planning bar + navigate button */}
      <div id="top-overlay" ref={topOverlayRef} className="absolute top-0 left-0 right-0 z-[500] pointer-events-none" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        {/* Relative wrapper so ModeToggle+Settings can be absolutely positioned at constant top-right */}
        <div className="relative">
          <DestinationInput />
          {/* Mode toggle + settings always float at the same top-right position regardless of form state */}
          <div className="absolute top-3 right-3 flex items-center gap-2 pointer-events-auto z-10">
            <ModeToggle />
            <SettingsButton />
          </div>
        </div>
        <NavigateBar />
      </div>

      {/* Layer 3: Add spot FAB */}
      <AddSpotFAB />

      {/* Layer 4: Bottom sheet — mosque list / trip plan */}
      <MapBottomSheet />

      {/* Layer 5: Detail sheets (mosque, spot, settings) */}
      <BottomSheet />
    </div>
  );
}

export default App;
